import React, { memo, useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowRight,
  Banknote,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Coffee,
  CreditCard,
  ExternalLink,
  Fingerprint,
  Layers,
  Mail,
  Menu,
  Package,
  Plane,
  Receipt,
  RotateCcw,
  RotateCw,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  TrendingDown,
  Users,
  Utensils,
  X,
  Zap,
  Star
} from 'lucide-react';
import Logo, { CleroS, CleroWordmarkSvg, PlaidVectorLogo, AstraVectorLogo, AstraMarkOnly, PlaidMarkOnly, BankSecurityVectorLogo } from './Logo';
import { auth, functions } from '../services/firebase';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../constants/downloadLinks';
import { AppStoreBadges, BottomAppShowcase, PageShell } from './public/PublicPrimitives';
import transactionsScreenPng from '../assets/screenshots/transactions_screen.png';
import recurringScreenPng from '../assets/screenshots/recurring_expense_details_screen.png';
import huluSubscriptionCropPng from '../assets/screenshots/hulu_subscription_crop.png';
import touchstoneClimbingCropPng from '../assets/screenshots/touchstone_climbing_crop.png';
import touchstoneRowCropPng from '../assets/screenshots/touchstone_row_crop.png';
import recurringDetailsCropPng from '../assets/screenshots/recurring_details_crop.png';
import groupsMonthlyScreenPng from '../assets/screenshots/groups_monthly_screen.png';
import groupsTripScreenPng from '../assets/screenshots/groups_trip_screen.png';
import groupsOndemandScreenPng from '../assets/screenshots/groups_ondemand_screen.png';
import groupsMonthlyCropPng from '../assets/screenshots/groups_monthly_settlement_crop.png';
import groupsTripCropPng from '../assets/screenshots/groups_trip_settlement_crop.png';
import groupsOndemandCropPng from '../assets/screenshots/groups_ondemand_settlement_crop.png';
import groupsSettledAlexPovPng from '../assets/screenshots/groups_settled_alex_pov.png';
import groupsSettledJohnPovPng from '../assets/screenshots/groups_settled_john_pov.png';

interface LandingPageProps {
  onStartDemo: () => void;
}

const COMPARISON_ROWS = [
  {
    feature: 'Direct Bank-to-Bank Transfers',
    clero: 'Yes (Same-Day Direct ACH)*',
    venmo: 'No (Held in wallet balance)',
    zelle: 'Yes (Direct bank)',
    splitwise: 'No (Tracking only)',
  },
  {
    feature: '1-to-1 Transfer & Deposit Fees',
    clero: '$0.00 (No cashout fees)',
    venmo: '1.75% Instant cashout fee',
    zelle: '$0.00',
    splitwise: 'N/A',
  },
  {
    feature: 'Money Lands in Bank Account',
    clero: 'Yes (Checking account)',
    venmo: 'No (Trapped in app wallet)',
    zelle: 'Yes',
    splitwise: 'N/A',
  },
  {
    feature: 'Credit Card Sync (Plaid)',
    clero: 'Yes (Visa, Mastercard, Amex)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'No',
  },
  {
    feature: '1-Tap Request from Posted Charge',
    clero: 'Yes (Pre-filled details)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'No',
  },
  {
    feature: 'Gmail AI Receipt Scanning',
    clero: 'Yes (Automatic itemization)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'No (Paywalled manual OCR)',
  },
  {
    feature: 'Uber / Lyft Rich Ride Extraction',
    clero: 'Yes (Stops, driver, tips)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'No',
  },
  {
    feature: 'Proportional Tax & Tip Math',
    clero: 'Yes (Calculated to cent)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'Limited / Paywalled',
  },
  {
    feature: 'Locked Paid Shares Protection',
    clero: 'Yes (Immutable state)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'No (Calculation drift)',
  },
  {
    feature: '1-Tap Batch Pay for Open Requests',
    clero: 'Yes (1 Bank transfer)',
    venmo: 'No (Must pay 1-by-1)',
    zelle: 'No',
    splitwise: 'No',
  },
  {
    feature: 'Roommate Monthly Cycle Rollover',
    clero: 'Yes (Automated 1st of month)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'No',
  },
  {
    feature: 'Group Debt Minimization',
    clero: 'Yes (Built-in netting)',
    venmo: 'No',
    zelle: 'No',
    splitwise: 'Yes (Paywalled Pro)',
  },
];

// Live receipt scanner animation stages (faithful to clero_app NewSplitChoiceScreen)
const RECEIPT_ANALYSIS_STAGES = [
  'Enhancing image…',
  'Balancing contrast…',
  'Reading line items…',
  'Detecting merchant…',
  'Matching dish prices…',
  'Extracting items…',
  'Normalizing tax & tip…',
  'Finalizing split details…',
];

// Isolated Scanner Widget component for zero-jank 60fps performance (prevents full page re-renders)
const ReceiptScannerWidget = memo(function ReceiptScannerWidget() {
  const [scanPercent, setScanPercent] = useState(82);
  const [scanStageIndex, setScanStageIndex] = useState(3);

  useEffect(() => {
    const interval = setInterval(() => {
      setScanPercent((prev) => {
        const next = prev >= 98 ? 14 : prev + 1;
        const stageIdx = Math.min(
          RECEIPT_ANALYSIS_STAGES.length - 1,
          Math.floor((next / 100) * RECEIPT_ANALYSIS_STAGES.length)
        );
        setScanStageIndex(stageIdx);
        return next;
      });
    }, 120);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center space-y-3 sm:space-y-4 w-full max-w-[260px] sm:max-w-none mx-auto">
      {/* Scanned Bill Photo */}
      <div className="relative w-full overflow-hidden rounded-2xl sm:rounded-[20px] will-change-transform shadow-md sm:shadow-none">
        <picture className="block w-full">
          <source srcSet="/screenshots/cafe_bill_raw.avif" type="image/avif" />
          <source srcSet="/screenshots/cafe_bill_raw.webp" type="image/webp" />
          <img
            alt="Scanned Cafe Du Soleil Bill"
            className="block h-auto w-full object-cover pointer-events-none"
            src="/screenshots/cafe_bill_raw.jpg"
          />
        </picture>

        {/* Teal Sweep Glow Aura */}
        <div
          className="absolute left-2 right-2 sm:left-3 sm:right-3 h-3 sm:h-4 rounded-full bg-teal-400/30 blur-xs animate-scan-sweep pointer-events-none will-change-transform"
        />

        {/* Teal Sweep Laser Line */}
        <div
          className="absolute left-2 right-2 sm:left-3 sm:right-3 h-1 sm:h-1.5 rounded-full bg-teal-400 shadow-[0_0_16px_#0D9488] animate-scan-sweep pointer-events-none will-change-transform"
        />
      </div>

      {/* Clean Progress Status & Bar (Shifted down with matched UI font and thickness) */}
      <div className="w-full px-0.5 space-y-1 sm:space-y-1.5 pt-0.5">
        <div className="flex items-center justify-between text-xs sm:text-sm font-bold text-slate-800">
          <span className="truncate pr-2">{RECEIPT_ANALYSIS_STAGES[scanStageIndex]}</span>
          <span className="font-extrabold text-teal-700 shrink-0">{scanPercent}%</span>
        </div>
        <div className="h-1.5 sm:h-2 w-full rounded-full bg-slate-200/80 overflow-hidden">
          <div
            className="h-full rounded-full bg-[var(--clero-primary)] transition-all duration-150 ease-linear shadow-[0_0_8px_rgba(13,148,136,0.35)]"
            style={{ width: `${scanPercent}%` }}
          />
        </div>
      </div>
    </div>
  );
});

// Live network traction metrics showcase with scroll-triggered count-up and custom SVG graphs
const LiveMetricsShowcase = memo(function LiveMetricsShowcase() {
  const SHOW_USERS_CARD = false;
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasStartedAnimation, setHasStartedAnimation] = useState(false);
  const [displayVolume, setDisplayVolume] = useState(0);
  const [displayMembers, setDisplayMembers] = useState(0);
  const [displayPayments, setDisplayPayments] = useState(0);
  const [displayHours, setDisplayHours] = useState(0);
  const [recentFlash, setRecentFlash] = useState<string | null>(null);

  const TARGET_VOLUME = 52840.65;
  const TARGET_MEMBERS = 68;
  const TARGET_PAYMENTS = 1284;
  const TARGET_HOURS = 412;

  // Scroll into view trigger
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStartedAnimation) {
          setHasStartedAnimation(true);
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasStartedAnimation]);

  // Smooth ease-out count-up animation on scroll entrance
  useEffect(() => {
    if (!hasStartedAnimation) return;

    let startTime: number | null = null;
    const duration = 1800; // 1.8s count up

    const animate = (now: number) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // Ease out expo curve
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

      setDisplayVolume(+(TARGET_VOLUME * ease).toFixed(2));
      if (SHOW_USERS_CARD) setDisplayMembers(Math.floor(TARGET_MEMBERS * ease));
      setDisplayPayments(Math.floor(TARGET_PAYMENTS * ease));
      setDisplayHours(Math.floor(TARGET_HOURS * ease));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayVolume(TARGET_VOLUME);
        if (SHOW_USERS_CARD) setDisplayMembers(TARGET_MEMBERS);
        setDisplayPayments(TARGET_PAYMENTS);
        setDisplayHours(TARGET_HOURS);
      }
    };

    const animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [hasStartedAnimation]);

  // Live real-time subtle increment ticker after count-up completes
  useEffect(() => {
    if (!hasStartedAnimation) return;

    const interval = setInterval(() => {
      const increments = [14.50, 18.00, 24.25, 9.00, 32.50, 48.00];
      const randomInc = increments[Math.floor(Math.random() * increments.length)];
      setDisplayVolume((prev) => +(prev + randomInc).toFixed(2));
      setDisplayPayments((prev) => prev + 1);
      setRecentFlash('volume');
      const timer = setTimeout(() => setRecentFlash(null), 1200);
      return () => clearTimeout(timer);
    }, 4500);

    return () => clearInterval(interval);
  }, [hasStartedAnimation]);

  return (
    <div ref={containerRef} className="grid grid-cols-2 gap-3 sm:gap-5 w-full max-w-[640px] mx-auto">
      {/* Tile 1: Volume Settled */}
      <div className={`relative overflow-hidden rounded-2xl sm:rounded-[26px] bg-white p-3.5 sm:p-6 transition-all duration-300 ${recentFlash === 'volume' ? 'ring-2 ring-emerald-500/30' : ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-[11px] sm:text-sm font-semibold text-slate-500 tracking-tight leading-snug">
            Volume settled
          </span>
          <span className="inline-flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-bold text-emerald-600">
            <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-emerald-600" />
            </span>
            Live
          </span>
        </div>
        <div className="mt-1 sm:mt-2 text-lg sm:text-3xl lg:text-[2rem] font-black text-slate-950 tracking-tight font-mono">
          ${displayVolume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        {/* Custom SVG Area Graph */}
        <div className="mt-2 sm:mt-4 h-8 sm:h-12 w-full">
          <svg className="h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 200 48">
            <defs>
              <linearGradient id="volGrad" x1="0%" x2="0%" y1="0%" y2="100%">
                <stop offset="0%" stopColor="#10B981" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#10B981" stopOpacity="0.0" />
              </linearGradient>
            </defs>
            <path
              d="M0,42 Q25,38 50,30 T100,24 T150,14 T200,6 L200,48 L0,48 Z"
              fill="url(#volGrad)"
            />
            <path
              d="M0,42 Q25,38 50,30 T100,24 T150,14 T200,6"
              fill="none"
              stroke="#10B981"
              strokeLinecap="round"
              strokeWidth="2.5"
            />
          </svg>
        </div>
      </div>

      {/* Users card hidden for now. Set SHOW_USERS_CARD to true to restore it. */}
      {SHOW_USERS_CARD && <div className="relative overflow-hidden rounded-2xl sm:rounded-[26px] bg-white p-3.5 sm:p-6">
        <div className="text-[11px] sm:text-sm font-semibold text-slate-500 tracking-tight leading-snug">
          Users
        </div>
        <div className="mt-1 sm:mt-2 text-lg sm:text-3xl lg:text-[2rem] font-black text-slate-950 tracking-tight font-mono">
          {displayMembers}
        </div>
        {/* Custom SVG Area Graph */}
        <div className="mt-2 sm:mt-4 h-8 sm:h-12 w-full">
          <svg className="h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 200 48">
            <defs>
              <linearGradient id="memGrad" x1="0%" x2="0%" y1="0%" y2="100%">
                <stop offset="0%" stopColor="#0D9488" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#0D9488" stopOpacity="0.0" />
              </linearGradient>
            </defs>
            <path
              d="M0,44 Q35,42 70,32 T140,18 T200,8 L200,48 L0,48 Z"
              fill="url(#memGrad)"
            />
            <path
              d="M0,44 Q35,42 70,32 T140,18 T200,8"
              fill="none"
              stroke="#0D9488"
              strokeLinecap="round"
              strokeWidth="2.5"
            />
          </svg>
        </div>
      </div>}

      {/* Tile 3: Payments & Requests */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[26px] bg-white p-3.5 sm:p-6">
        <div className="text-[11px] sm:text-sm font-semibold text-slate-500 tracking-tight leading-snug">
          Payments & requests
        </div>
        <div className="mt-1 sm:mt-2 text-lg sm:text-3xl lg:text-[2rem] font-black text-slate-950 tracking-tight font-mono">
          {displayPayments.toLocaleString()}
        </div>
        {/* Custom SVG Area Graph */}
        <div className="mt-2 sm:mt-4 h-8 sm:h-12 w-full">
          <svg className="h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 200 48">
            <defs>
              <linearGradient id="payGrad" x1="0%" x2="0%" y1="0%" y2="100%">
                <stop offset="0%" stopColor="#6366F1" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#6366F1" stopOpacity="0.0" />
              </linearGradient>
            </defs>
            <path
              d="M0,40 Q30,36 60,38 T120,20 T170,12 T200,4 L200,48 L0,48 Z"
              fill="url(#payGrad)"
            />
            <path
              d="M0,40 Q30,36 60,38 T120,20 T170,12 T200,4"
              fill="none"
              stroke="#6366F1"
              strokeLinecap="round"
              strokeWidth="2.5"
            />
          </svg>
        </div>
      </div>

      {/* Tile 4: Time Saved vs Venmo & Splitwise */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[26px] bg-white p-3.5 sm:p-6">
        <div className="text-[11px] sm:text-sm font-semibold text-slate-500 tracking-tight leading-snug">
          Time saved vs apps
        </div>
        <div className="mt-1 sm:mt-2 text-lg sm:text-3xl lg:text-[2rem] font-black text-slate-950 tracking-tight font-mono">
          {displayHours}+ <span className="text-xs sm:text-lg font-bold text-slate-500 font-sans">hrs</span>
        </div>
        {/* Custom SVG Area Graph */}
        <div className="mt-2 sm:mt-4 h-8 sm:h-12 w-full">
          <svg className="h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 200 48">
            <defs>
              <linearGradient id="timeGrad" x1="0%" x2="0%" y1="0%" y2="100%">
                <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.0" />
              </linearGradient>
            </defs>
            <path
              d="M0,45 Q40,44 80,36 T150,16 T200,5 L200,48 L0,48 Z"
              fill="url(#timeGrad)"
            />
            <path
              d="M0,45 Q40,44 80,36 T150,16 T200,5"
              fill="none"
              stroke="#F59E0B"
              strokeLinecap="round"
              strokeWidth="2.5"
            />
          </svg>
        </div>
      </div>
    </div>
  );
});

// Combined Participant Claim Video & Synchronized Live Organizer Sync Showcase
const ClaimAndSettleShowcase = memo(function ClaimAndSettleShowcase() {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [currentAmount, setCurrentAmount] = useState(111.32);
  const [currentPercent, setCurrentPercent] = useState(0);
  const [johnState, setJohnState] = useState<'initial' | 'claiming' | 'claimed' | 'paying' | 'paid'>('initial');
  const [johnDisplayAmount, setJohnDisplayAmount] = useState(0.00);
  const hasTriggeredRef = useRef(false);
  const hasClaimedRef = useRef(false);

  const START_AMOUNT = 111.32;
  const TARGET_AMOUNT = 96.77;
  const TARGET_PERCENT = 13;
  const TARGET_JOHN_AMOUNT = 14.82;

  useEffect(() => {
    // Autoplay when mounted or when scrolled into view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoRef.current?.play().catch(() => {});
          } else {
            videoRef.current?.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const triggerCountdown = () => {
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    const duration = 1400; // 1.4s smooth countdown transition
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // Ease-out cubic curve
      const ease = 1 - Math.pow(1 - progress, 3);

      const newAmount = +(START_AMOUNT - (START_AMOUNT - TARGET_AMOUNT) * ease).toFixed(2);
      const newPercent = Math.round(TARGET_PERCENT * ease);

      setCurrentAmount(newAmount);
      setCurrentPercent(newPercent);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setCurrentAmount(TARGET_AMOUNT);
        setCurrentPercent(TARGET_PERCENT);
      }
    };

    requestAnimationFrame(tick);
  };

  const animateJohnClaim = () => {
    if (hasClaimedRef.current) return;
    hasClaimedRef.current = true;
    setJohnState('claiming');

    const duration = 750; // 750ms smooth count-up from $0.00 to $14.82
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const ease = 1 - Math.pow(1 - progress, 3);
      const val = +(TARGET_JOHN_AMOUNT * ease).toFixed(2);
      setJohnDisplayAmount(val);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setJohnDisplayAmount(TARGET_JOHN_AMOUNT);
        setJohnState('claimed');
      }
    };

    requestAnimationFrame(tick);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const time = videoRef.current.currentTime;

    // 1. At 4.0s mark: John claims dishes -> smooth animated count-up from $0.00 to $14.82
    if (time >= 4.0 && time < 11.0 && !hasClaimedRef.current) {
      animateJohnClaim();
    }

    // 2. At 11.0s mark: John pays -> animate transition into Paid section & countdown organizer card
    if (time >= 11.0 && !hasTriggeredRef.current) {
      setJohnState('paying');
      triggerCountdown();
      setTimeout(() => {
        setJohnState('paid');
      }, 300);
    }

    // 3. Reset when video loops back to start
    if (time < 1.0 && (hasTriggeredRef.current || hasClaimedRef.current)) {
      hasTriggeredRef.current = false;
      hasClaimedRef.current = false;
      setCurrentAmount(START_AMOUNT);
      setCurrentPercent(0);
      setJohnState('initial');
      setJohnDisplayAmount(0.00);
    }
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      // Pause at end of payment confirmation for 1.4s before restarting loop
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          hasTriggeredRef.current = false;
          hasClaimedRef.current = false;
          setCurrentAmount(START_AMOUNT);
          setCurrentPercent(0);
          setJohnState('initial');
          setJohnDisplayAmount(0.00);
          setTimeout(() => {
            videoRef.current?.play().catch(() => {});
          }, 400);
        }
      }, 1400);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-stretch items-center justify-center gap-6 sm:gap-8 max-w-[660px] mx-auto w-full">
      {/* Column 1 (Left on Desktop, Bottom on Mobile): Live Organizer Split Hero Card & Participants List (Height aligned with phone) */}
      <div className="order-2 md:order-1 w-full max-w-[255px] sm:max-w-[275px] flex flex-col justify-between shrink-0 py-0.5">
        <div>
          {/* Header Title: Aligned with the top of the phone frame */}
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 text-left mb-2 sm:mb-2.5">
            Organizer&apos;s view
          </div>

          {/* 1. Organizer Hero Card */}
          <div className="w-full rounded-[22px] sm:rounded-[24px] p-4 bg-gradient-to-br from-[#0D1D2E] to-[#475569] text-white border border-white/10">
            {/* Top Header: Label & Active Badge */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-300 tracking-tight">
                Remaining to receive
              </span>
              <span className="inline-flex items-center rounded-full border border-white/30 bg-white/15 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-xs">
                Active
              </span>
            </div>

            {/* Main Hero Amount (Synchronized Animated Countdown at 11s) */}
            <div className="mt-1.5 flex items-baseline">
              <span className="text-2xl sm:text-[1.85rem] font-extrabold text-white tracking-tight font-mono">
                ${currentAmount.toFixed(2)}
              </span>
            </div>

            {/* Progress Track & Fill */}
            <div className="mt-3 h-[4px] w-full rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--clero-primary)] transition-all duration-75 ease-out shadow-[0_0_8px_rgba(13,148,136,0.5)]"
                style={{ width: `${currentPercent}%` }}
              />
            </div>

            {/* Progress Label */}
            <div className="mt-1 text-[11px] font-medium text-slate-200">
              {currentPercent}% collected
            </div>

            {/* Divider */}
            <div className="mt-3 pt-3 border-t border-white/15">
              {/* Footer Two-Column Stats */}
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <div className="text-[10px] font-medium text-slate-300">Total expected</div>
                  <div className="mt-0.5 text-sm sm:text-base font-bold text-white font-mono">$111.32</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-medium text-slate-300">Your portion</div>
                  <div className="mt-0.5 text-sm sm:text-base font-bold text-white font-mono">$25.59</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Recreated App SplitDashboard Participants List (Generously spaced, fixed stable height) */}
        <div className="mt-3.5 w-full flex-1 flex flex-col justify-between rounded-[22px] sm:rounded-[24px] p-4 bg-white border border-slate-200/70 text-slate-900 shadow-xs">
          <div className="flex-1 flex flex-col justify-between">
            <div>
              {/* Header */}
              <div className="pb-1">
                <span className="text-xs sm:text-sm font-bold text-slate-950">Participants</span>
              </div>

              {/* Organizer: Alex Sharp (Faded for focus on John) */}
              <div className="flex items-center justify-between py-1.5 opacity-40 transition-opacity duration-300">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-slate-200/90 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                    AS
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-900 leading-tight">Alex Sharp</p>
                    <p className="text-[10px] font-bold text-teal-600 leading-tight">Organizer</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-slate-900 font-mono">$25.59</span>
              </div>

              {/* Section: Awaiting Payment */}
              <div className="mt-2.5 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-slate-500">
                    Awaiting payment
                  </span>
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.2 text-[9px] font-bold text-slate-600">
                    {johnState === 'paid' || johnState === 'paying' ? 4 : 5}
                  </span>
                </div>

                <div className="space-y-2 sm:space-y-2.5">
                  {/* Amy Kim (Faded) */}
                  <div className="flex items-center justify-between opacity-40 transition-opacity duration-300">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-amber-400 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        AK
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-900 leading-tight">Amy Kim</p>
                        <p className="text-[9.5px] text-slate-400 leading-tight">Awaiting payment</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-slate-900 font-mono">$0.00</span>
                  </div>

                  {/* Anna Haro (Faded) */}
                  <div className="flex items-center justify-between opacity-40 transition-opacity duration-300">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-sky-400 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        AH
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-900 leading-tight">Anna Haro</p>
                        <p className="text-[9.5px] text-slate-400 leading-tight">Awaiting payment</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-slate-900 font-mono">$7.08</span>
                  </div>

                  {/* Daniel Higgins Jr. (Faded) */}
                  <div className="flex items-center justify-between opacity-40 transition-opacity duration-300">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-emerald-400 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        DH
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-900 leading-tight truncate max-w-[85px]">Daniel Higgins</p>
                        <p className="text-[9.5px] text-slate-400 leading-tight">Awaiting payment</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-slate-900 font-mono">$4.90</span>
                  </div>

                  {/* David Taylor (Faded) */}
                  <div className="flex items-center justify-between opacity-40 transition-opacity duration-300">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-purple-400 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        DT
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-900 leading-tight">David Taylor</p>
                        <p className="text-[9.5px] text-slate-400 leading-tight">Awaiting payment</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-slate-900 font-mono">$0.00</span>
                  </div>

                  {/* John Doe (Active Animated Row: in Awaiting when initial / claiming / claimed) */}
                  {johnState !== 'paid' && (
                    <div
                      className={`flex items-center justify-between transition-all duration-300 ${
                        johnState === 'paying'
                          ? 'opacity-0 scale-95'
                          : johnState === 'claiming' || johnState === 'claimed'
                          ? 'opacity-100 bg-teal-50 -mx-1 px-1 py-0.5 rounded-lg ring-1 ring-teal-500/25 shadow-xs'
                          : 'opacity-100'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-slate-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                          JD
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-950 leading-tight">John Doe</p>
                          <p className="text-[9.5px] text-slate-500 leading-tight">Awaiting payment</p>
                        </div>
                      </div>
                      <span
                        className={`text-[11px] font-mono transition-all duration-200 ${
                          johnState === 'claiming' || johnState === 'claimed'
                            ? 'text-teal-700 font-black scale-105'
                            : 'text-slate-900 font-bold scale-100'
                        }`}
                      >
                        ${johnDisplayAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section: Paid (Shown smoothly when John Doe pays at 11s) */}
            {johnState === 'paid' && (
              <div className="mt-2.5 pt-2 border-t border-slate-100 animate-in fade-in slide-in-from-top-1 duration-300">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-slate-500">
                    Paid
                  </span>
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[9px] font-bold text-emerald-800">
                    1
                  </span>
                </div>

                <div className="flex items-center justify-between opacity-100 bg-emerald-50/60 -mx-1 px-1 py-0.5 rounded-lg ring-1 ring-emerald-500/20">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-slate-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                      JD
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-950 leading-tight">John Doe</p>
                      <p className="text-[9.5px] font-bold text-teal-600 leading-tight">Paid</p>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-emerald-700 font-mono">$14.82</span>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Column 2 (Right on Desktop, Top on Mobile): Participant Phone Video */}
      <div className="order-1 md:order-2 w-full max-w-[270px] sm:max-w-[295px] shrink-0 self-center">
        <div className="relative flex items-center justify-center w-full mx-auto py-2">
          {/* Background Soft Ambient Radial Glow */}
          <div className="absolute inset-0 m-auto w-[260px] sm:w-[290px] h-[260px] sm:h-[290px] rounded-full bg-emerald-200/40 blur-3xl pointer-events-none" />

          {/* iPhone 16 Pro Shell */}
          <div
            className="relative mx-auto w-full"
            style={{ aspectRatio: '1022 / 2082' }}
          >
            <picture>
              <source srcSet="/mockup.avif" type="image/avif" />
              <source srcSet="/mockup.webp" type="image/webp" />
              <img
                alt="iPhone 16 Pro Frame Shell"
                className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(2,8,23,0.22)]"
                draggable={false}
                src="/mockup.png"
              />
            </picture>

            {/* Inner Screen Display */}
            <div
              className="absolute z-10 overflow-hidden bg-slate-900"
              style={{
                left: '5.088%',
                top: '2.209%',
                width: '89.824%',
                height: '95.581%',
                borderRadius: '13.725% / 6.332%',
              }}
            >
              {/* Instant Static First Frame Poster (Zero Black Screen Flicker) */}
              <picture>
                <source srcSet="/videos/claimandpay_poster.avif" type="image/avif" />
                <source srcSet="/videos/claimandpay_poster.webp" type="image/webp" />
                <img
                  alt="Clero Claim and Pay Screen"
                  className="absolute inset-0 block h-full w-full object-cover object-top z-10 pointer-events-none"
                  draggable={false}
                  src="/videos/claimandpay_poster.png"
                />
              </picture>

              {/* Hardware-Accelerated Video Layer (Fades In Smoothly) */}
              <video
                ref={videoRef}
                className={`absolute inset-0 block h-full w-full object-cover object-top z-20 transition-opacity duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                  isVideoReady ? 'opacity-100' : 'opacity-0'
                }`}
                muted
                onEnded={handleVideoEnded}
                onPlaying={() => setIsVideoReady(true)}
                onTimeUpdate={handleTimeUpdate}
                playsInline
                preload="auto"
              >
                <source src="/videos/claimandpay.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Authentic Brand Logos
 */
const GmailBrandIcon: React.FC<{ className?: string }> = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.272H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L12 9.545l8.073-6.052C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335" />
    <path d="M0 5.457v13.909c0 .904.732 1.636 1.636 1.636h3.819V11.73L0 7.636V5.457z" fill="#4285F4" />
    <path d="M18.545 11.73v9.272h3.819c.904 0 1.636-.732 1.636-1.636V5.457l-5.455 6.273z" fill="#34A853" />
    <path d="M3.927 3.493C2.309 2.279 0 3.434 0 5.457l5.455 4.09 6.545-4.91-8.073-1.144z" fill="#FBBC05" />
  </svg>
);

const StarbucksSirenSVG: React.FC<{ className?: string }> = ({ className = "h-full w-full" }) => (
  <svg className={className} role="img" viewBox="0 0 24 24" fill="#006241" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.2072 5.2801c-.1052-.0188-.6126-.104-1.2072-.104s-1.102.0848-1.2072.104c-.0605.0108-.0837-.0484-.0377-.0828.0417-.0308 1.2445-.9463 1.2445-.9463l1.244.9463c.0469.0344.024.0936-.0364.0828zm-2.0783 5.9446s-.0636.0228-.0804.0788c.252.1937.4257.6343.9515.6343s.6995-.4406.9511-.6343c-.0164-.0564-.08-.0788-.08-.0788s-.3293.0776-.8711.0776c-.5418 0-.8711-.0776-.8711-.0776zM12 10.4832c-.146 0-.178-.0552-.2777-.0548-.0948.0004-.2789.076-.319.1453a.1542.1542 0 00.0413.0948c.2129.032.309.1505.5558.1505.2469 0 .3425-.1185.5558-.1505a.1579.1579 0 00.0412-.0948c-.0396-.0692-.224-.1445-.3193-.1453-.1-.0008-.1324.0548-.2781.0548zm11.9868 2.27a11.964 11.964 0 01-.076.8528c-1.359.2249-1.8447-.986-3.2368-.9252.0832.2954.1508.5963.2029.9036 1.148-.0008 1.6105 1.0724 2.8878.9143-.0672.3281-.148.6519-.2413.9708-1.01.0992-1.3657-.9044-2.5345-.8767.0096.1664.0148.3345.0148.5041l-.0048.2805 2.2696.866a12.04 12.04 0 01-.3965.9479c-.6823-.0376-.9175-.9127-1.9555-.8431a9.0882 9.0882 0 01-.118.665c.9015-.0632 1.0955.7667 1.7414.834a12.2317 12.2317 0 01-.5302.8767c-.3826-.205-.7143-.8231-1.4398-.8612a8.6035 8.6035 0 00.195-.6994c-.6435 0-1.3794-.2509-1.9964-.8127.2-1.1388-1.5674-2.2984-1.5674-3.1324 0-.9059.4582-1.4073.4582-2.6285 0-.9063-.4402-1.8895-1.104-2.5614a2.2175 2.2175 0 00-.4114-.3309c.6098.7547 1.078 1.6494 1.078 2.6858 0 1.15-.535 1.757-.535 2.8186 0 1.0612 1.5526 1.9796 1.5526 3.074 0 .4305-.1377.851-.5914 1.677.697.6962 1.605 1.076 2.1908 1.076.19 0 .292-.058.3601-.2073a9.0925 9.0925 0 00.1665-.391c.631.0245.9199.5979 1.2692.8268-.1916.2573-.393.5057-.6038.7462-.234-.2593-.5486-.6954-1.0092-.8167a9.2087 9.2087 0 01-.2613.473c.3966.108.6679.5082.878.7715a12.1305 12.1305 0 01-.7075.6754c-.1532-.2384-.3917-.541-.659-.7042a8.3639 8.3639 0 01-.3077.391c.2272.154.4277.4313.5586.6574-.2833.2272-.5763.443-.8796.6446-.1496-1.2192-1.8138-2.0548-1.3653-3.4693-.1472.2493-.3229.561-.3229.9364 0 1.024 1.0908 1.8366 1.1776 2.8542-.226.1353-.4573.2625-.693.383-.0392-1.1185-1.194-2.3425-1.194-3.2604 0-1.0248 1.342-2.054 1.342-3.2636 0-1.2105-1.5485-2.0484-1.5485-3.1112 0-1.062.6586-1.673.6586-3.0343 0-.9972-.4738-2.0063-1.2056-2.651-.1297-.1144-.2573-.2052-.4106-.2849.6903.828 1.0904 1.579 1.0904 2.7186 0 1.2801-.7546 1.9908-.7546 3.244 0 1.2537 1.5197 1.9507 1.5197 3.1192 0 1.1684-1.4145 2.1532-1.4145 3.3536 0 1.092 1.2468 2.182 1.2653 3.4777a11.7704 11.7704 0 01-.8327.3257c.1584-1.3089-1.245-2.659-1.245-3.727 0-1.1676 1.4674-2.1712 1.4674-3.43 0-1.2597-1.4917-1.8451-1.4917-3.138 0-1.292.9151-2.0075.9151-3.4352 0-1.1129-.5494-2.1136-1.352-2.7338l-.0509-.0385c-.0756-.056-.138.0116-.0844.078.5682.7095.8719 1.427.8719 2.4894 0 1.306-1.0512 2.3673-1.0512 3.6325 0 1.4934 1.4117 1.9203 1.4117 3.1456 0 1.2248-1.5137 2.2048-1.5137 3.5053 0 1.206 1.4325 2.5445 1.1868 3.9366a11.6645 11.6645 0 01-.8743.192c.2689-1.7334-1.1364-2.9782-1.1364-4.1122 0-1.2277 1.5677-2.322 1.5677-3.5217 0-1.1316-1.1252-1.5014-1.2732-2.659-.0204-.158-.1473-.2753-.3221-.2461-.2285.0416-.5214.192-.9816.192-.4602 0-.753-.1508-.982-.192-.1744-.0288-.3013.0884-.3217.246-.1476 1.1577-1.2736 1.527-1.2736 2.659 0 1.1997 1.5681 2.2937 1.5681 3.5218 0 1.134-1.4053 2.3788-1.1368 4.1123a12.1233 12.1233 0 01-.8743-.1921c-.2457-1.3921 1.1872-2.7306 1.1872-3.9366 0-1.3005-1.5141-2.2805-1.5141-3.5053 0-1.2253 1.412-1.6522 1.412-3.1456 0-1.2652-1.0515-2.326-1.0515-3.6325 0-1.062.3037-1.7795.8723-2.4893.0532-.0665-.0088-.134-.0848-.078l-.0504.0384c-.802.6186-1.351 1.6193-1.351 2.7322 0 1.4277.9152 2.1431.9152 3.4352 0 1.2925-1.4917 1.878-1.4917 3.138 0 1.2588 1.4673 2.2624 1.4673 3.43 0 1.0684-1.4033 2.4181-1.2445 3.727a11.8995 11.8995 0 01-.833-.3257c.0188-1.2957 1.2648-2.3861 1.2648-3.4777 0-1.2004-1.4137-2.1852-1.4137-3.3536 0-1.1685 1.519-1.8655 1.519-3.1192 0-1.2532-.7543-1.9639-.7543-3.244 0-1.1396.3997-1.8907 1.09-2.7186-.1537.0797-.281.1705-.4102.285-.7318.6446-1.2052 1.6537-1.2052 2.651 0 1.3612.6586 1.9722.6586 3.0342 0 1.0628-1.5485 1.9007-1.5485 3.1112 0 1.2096 1.342 2.2392 1.342 3.2636 0 .9183-1.1556 2.1423-1.1944 3.2604a11.8754 11.8754 0 01-.693-.383c.0872-1.0176 1.1776-1.8303 1.1776-2.8542 0-.3754-.1753-.687-.323-.9364.4486 1.4145-1.2156 2.25-1.3652 3.4693a12.1204 12.1204 0 01-.8796-.6446c.1305-.2257.331-.5034.5586-.6575a7.9134 7.9134 0 01-.3077-.391c-.2677.1633-.5066.4659-.6594.7043a12.2459 12.2459 0 01-.707-.6754c.21-.2633.4813-.6635.8779-.7715a9.0433 9.0433 0 01-.2613-.473c-.4606.1213-.7755.5574-1.0092.8167a12.141 12.141 0 01-.6038-.7462c.3493-.229.6382-.8027 1.2688-.8267.0529.1312.1085.2617.1669.3909.068.1493.1705.2073.3601.2073.5858 0 1.4934-.3798 2.1908-1.076-.4537-.826-.5914-1.2465-.5914-1.677 0-1.094 1.5526-2.0124 1.5526-3.074 0-1.0615-.535-1.6686-.535-2.8186 0-1.0364.4682-1.9311 1.078-2.6858a2.2175 2.2175 0 00-.4114.331c-.6638.6722-1.104 1.655-1.104 2.5613 0 1.2212.4586 1.7226.4586 2.6285 0 .834-1.7679 1.9936-1.5678 3.1324-.617.5618-1.3529.8127-1.9967.8127a9.305 9.305 0 00.1949.6994c-.7251.0385-1.0568.6567-1.4398.8612a12.0872 12.0872 0 01-.5302-.8768c.6455-.0672.84-.897 1.7419-.8339a9.1275 9.1275 0 01-.1185-.665c-1.038-.0696-1.2732.8059-1.9555.8431a12.04 12.04 0 01-.3965-.948l2.27-.8659-.0048-.2805c0-.1696.0052-.3377.0144-.5041-1.1688-.0273-1.5246.976-2.5345.8767a12.106 12.106 0 01-.241-.9708c1.2766.158 1.7395-.9151 2.888-.9143a8.7482 8.7482 0 01.2024-.9036c-1.392-.0604-1.8779 1.1505-3.2364.9252a11.7352 11.7352 0 01-.076-.8527c1.5794.1764 2.1716-1.122 3.6097-.9632a8.4303 8.4303 0 01.471-.9963c-1.803-.317-2.4153 1.1908-4.0935.9591C.1813 5.2805 5.4844.0898 12 .0898S23.8187 5.2805 24 11.753c-1.6786.2317-2.2908-1.2757-4.0935-.9591.1773.32.335.6526.471.9963 1.4373-.1592 2.0295 1.1396 3.6093.9632zm-17.147-5.035c-.884-.3613-1.954-.278-2.868.309-.1416-.8504-.603-1.6058-1.26-2.0616-.0908-.0628-.1853-.0032-.1769.102.1389 1.7967-.9115 3.3569-2.2032 4.7282 1.3313.4001 2.4645-1.3141 4.1912-.7159a9.0364 9.0364 0 012.3168-2.3617zM12 6.6314c-1.1144 0-2.048.6303-2.2924 1.4446-.0188.0624.0068.1028.0788.0704.2005-.09.4285-.1333.6762-.1333.4546 0 .8551.1669 1.092.4574.1049.3457.1137.8463-.0048 1.132-.1872-.042-.2545-.1868-.4373-.1868-.1829 0-.3245.1284-.6347.1284-.3097 0-.346-.1465-.5498-.1465-.2396 0-.2837.247-.2837.5254 0 1.2417 1.1413 2.9503 2.3553 2.9503 1.2136 0 2.3549-1.7086 2.3549-2.9503 0-.2785-.0573-.517-.3077-.5494-.1249.09-.2397.1705-.5254.1705-.3102 0-.3958-.1284-.5783-.1284-.2204 0-.1984.465-.4601.491-.1745-.4194-.1829-.9572-.038-1.4362.2373-.2905.6374-.4574 1.092-.4574.2477 0 .4773.0437.6758.1333.0724.0324.0976-.0084.0788-.0704-.244-.8143-1.1772-1.4446-2.2916-1.4446zm1.7743 1.7815c-.2673 0-.5807.082-.7775.3013-.0204.0596-.0204.1484.0084.2077.4845-.166.9119-.1725 1.1184.0584.11-.1.1452-.19.1452-.2945 0-.1613-.164-.273-.4945-.273zm-3.8979.5674c.2337-.234.7263-.224 1.238-.0352.0225-.2545-.4333-.5326-.8887-.5326-.3309 0-.4945.1116-.4945.2733 0 .1044.0352.1949.1452.2945zm7.6804-4.2031c-.8799.0628-1.6442.3649-2.2624.8683.2625-.7443.5958-1.3953 1.0184-2.0264-1.1204.1189-2.0572.5286-2.7406 1.2289l-.535-1.4025 1.1876-1.0488-1.5902-.1125L12 .8053l-.635 1.479-1.5902.1124 1.1876 1.0488-.5346 1.4025c-.6838-.7003-1.6206-1.11-2.7402-1.2289.4218.6315.7551 1.2825 1.0176 2.0264-.6178-.5038-1.3821-.806-2.262-.8683.5278.6786.9955 1.402 1.342 2.18.0393.0876.1233.1164.2141.0712 1.2053-.599 2.5634-.936 4.0003-.936 1.437 0 2.7946.3374 4.0007.936.0908.0452.1748.0164.2136-.0712.347-.778.8147-1.5014 1.343-2.18zm1.9211 5.3035c1.7259-.5982 2.8595 1.1156 4.1908.7159-1.2917-1.3713-2.3417-2.9315-2.2028-4.7282.0084-.1052-.0865-.1652-.1769-.102-.6574.4558-1.1188 1.2112-1.26 2.0615-.9144-.587-1.984-.6706-2.868-.3089a9.0431 9.0431 0 012.317 2.3617z"/>
  </svg>
);

const InstacartCarrotSVG: React.FC<{ className?: string }> = ({ className = "h-full w-full" }) => (
  <svg className={className} viewBox="0 0 19 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.14 4.08c-1.03 0-1.78.45-2.45 1.43L11.72 8.33V.28H7.03v8.05L5.07 5.51C4.39 4.53 3.65 4.08 2.62 4.08 1.05 4.08 0 5.23 0 6.66c0 1.21.57 2.01 1.78 2.79l7.59 4.88 7.59-4.88c1.21-.78 1.78-1.58 1.78-2.79 0-1.43-1.05-2.58-2.6-2.58Z" fill="#0AAD0A"/>
    <path d="M9.37 15.5c4.51 0 8.21 3.37 8.2 8.2H1.17c-.01-4.83 3.69-8.2 8.2-8.2Z" fill="#FF7009"/>
  </svg>
);

/**
 * Premium Gmail Receipt Suggestions Showcase:
 * - Fixed static alignment: Accounts & Marquee never shift when cards populate
 * - Title is completely anchored: Never moves up or down
 * - Downward expansion: Uber Eats expands downwards, with ample bottom clearance so Instacart is never cut off
 * - In-place icon replacement: Category icon replaces with checkmark on selection (zero sibling tile shift)
 * - Deterministic pill status: Strict 1-to-1 sync between suggestions visible and pill count
 * - Unified Animated Reset: All 3 suggestion rows fade away simultaneously, email tiles unselect in one go
 */
const GmailReceiptMachineShowcase = memo(function GmailReceiptMachineShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState<boolean>(false);

  // States:
  // 0: Scanning inboxes, marquee moving, skeletons shown, pill "Scanning inboxes…"
  // 1: Step 1 -> Uber Eats selected in Row 1 -> Uber Eats suggestion slides in ("1 receipt found")
  // 2: Step 2 -> Starbucks selected in Row 2 -> Starbucks suggestion slides in ("2 receipts found")
  // 3: Step 3 -> Instacart selected in Row 3 -> Instacart suggestion slides in ("3 receipts found")
  // 4: Step 4 -> Uber Eats expands "Create request" ("3 receipts found")
  // 5: Step 5 -> Unified simultaneous fadeout of all suggestions back to skeletons
  const [animStep, setAnimStep] = useState<number>(0);

  // Start sequence exactly when scrolled into view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasScrolledIntoView(true);
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasScrolledIntoView) return;

    const timeouts: NodeJS.Timeout[] = [];

    const runLoop = () => {
      // 0.0s: Brief initial scan state (0.8s total)
      setAnimStep(0);

      // 0.8s: Uber Eats detected & slides in
      timeouts.push(setTimeout(() => {
        setAnimStep(1);
      }, 800));

      // 2.0s: Starbucks detected & slides in
      timeouts.push(setTimeout(() => {
        setAnimStep(2);
      }, 2000));

      // 3.2s: Instacart detected & slides in
      timeouts.push(setTimeout(() => {
        setAnimStep(3);
      }, 3200));

      // 4.4s: Uber Eats expands CTA to "Create request"
      timeouts.push(setTimeout(() => {
        setAnimStep(4);
      }, 4400));

      // 10.0s: Unified Reset (after 5.6s of holding the expanded payoff state)
      timeouts.push(setTimeout(() => {
        setAnimStep(5);
      }, 10000));

      // 10.6s: Seamless loop restart to Step 0
      timeouts.push(setTimeout(() => {
        runLoop();
      }, 10600));
    };

    runLoop();

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [hasScrolledIntoView]);

  // Derived state helpers (strictly in sync)
  const row1Selected = animStep >= 1 && animStep < 5;
  const row2Selected = animStep >= 2 && animStep < 5;
  const row3Selected = animStep >= 3 && animStep < 5;
  const uberEatsExpanded = animStep === 4;
  const showUberEats = animStep >= 1 && animStep < 5;
  const showStarbucks = animStep >= 2 && animStep < 5;
  const showInstacart = animStep >= 3 && animStep < 5;

  const pillText =
    animStep === 1
      ? '1 receipt found'
      : animStep === 2
      ? '2 receipts found'
      : animStep === 3 || animStep === 4
      ? '3 receipts found'
      : 'Scanning inboxes…';

  const pillStyle =
    animStep === 3 || animStep === 4
      ? 'bg-slate-950 text-white shadow-xs'
      : animStep === 1 || animStep === 2
      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200/60'
      : 'bg-slate-100 text-slate-400';

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-full lg:max-w-[580px] mx-auto flex flex-col items-center justify-center select-none min-w-0"
    >
      {/* Background Soft Ambient Glow */}
      <div className="absolute inset-0 m-auto w-[320px] sm:w-[480px] h-[320px] sm:h-[480px] rounded-full bg-gradient-to-tr from-teal-100/30 via-emerald-50/20 to-cyan-100/20 blur-3xl pointer-events-none" />

      {/* Main Container Card — Fixed Height with generous bottom clearance */}
      <div className="relative w-full max-w-full h-[570px] sm:h-[620px] rounded-[28px] sm:rounded-[36px] bg-white/95 backdrop-blur-xl p-3.5 sm:p-6 pb-5 sm:pb-7 shadow-[0_20px_50px_rgba(15,23,42,0.06)] border border-slate-100/90 flex flex-col justify-start overflow-hidden min-w-0">
        
        {/* TOP LAYER: Personal & Work Inboxes (Stationary) */}
        <div className="w-full max-w-full h-[80px] sm:h-[96px] shrink-0 mb-2 sm:mb-3 min-w-0">
          <div className="w-full flex items-center justify-between mb-1.5 sm:mb-2.5 px-0.5">
            <span className="text-[11px] sm:text-xs font-semibold text-slate-500">
              Connected Gmail Inboxes
            </span>
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[10.5px] sm:text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                2/3 Connected
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3 min-w-0 w-full">
            {/* Personal Inbox Tile */}
            <div className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-2xl bg-slate-50/90 border border-slate-100/90 shadow-xs min-w-0">
              <div className="flex h-7.5 w-7.5 sm:h-8.5 sm:w-8.5 items-center justify-center rounded-xl bg-white shadow-xs shrink-0 border border-slate-100">
                <GmailBrandIcon className="h-3.5 w-3.5 sm:h-4.5 sm:w-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs sm:text-sm font-bold text-slate-900 truncate">Personal</div>
                <div className="text-[9.5px] sm:text-[11px] text-slate-400 truncate">Primary Inbox</div>
              </div>
            </div>

            {/* Work Inbox Tile */}
            <div className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-2xl bg-slate-50/90 border border-slate-100/90 shadow-xs min-w-0">
              <div className="flex h-7.5 w-7.5 sm:h-8.5 sm:w-8.5 items-center justify-center rounded-xl bg-white shadow-xs shrink-0 border border-slate-100">
                <GmailBrandIcon className="h-3.5 w-3.5 sm:h-4.5 sm:w-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs sm:text-sm font-bold text-slate-900 truncate">Work</div>
                <div className="text-[9.5px] sm:text-[11px] text-slate-400 truncate">Work & Expenses</div>
              </div>
            </div>
          </div>
        </div>

        {/* MIDDLE LAYER: Borderless Moving Marquee Streams (Stationary Position) */}
        <div className="relative w-full h-[112px] sm:h-[128px] shrink-0 mb-2.5 sm:mb-4 py-0.5 overflow-hidden flex flex-col justify-between">
          {/* Side Fade Gradient Masks (Left & Right Faded) */}
          <div className="absolute inset-y-0 left-0 w-8 sm:w-16 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-8 sm:w-16 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />

          {/* ROW 1: Constantly Moves Right -> */}
          <div className="w-0 min-w-full overflow-hidden py-0.5">
            <div className="flex space-x-2 anim-marquee-right-infinite whitespace-nowrap w-max will-change-transform">
              {[0, 1].map((copyIdx) => (
                <React.Fragment key={`r1-${copyIdx}`}>
                  {/* Uber Eats Tile — In-place checkmark replacement on selection */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors duration-300 shrink-0 ${
                    row1Selected
                      ? 'bg-emerald-50/95 text-emerald-900 border-emerald-500 shadow-xs ring-1 ring-emerald-500/20'
                      : 'bg-white/90 border-slate-200/70 text-slate-700'
                  }`}>
                    {row1Selected ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 stroke-[2.5]" />
                    ) : (
                      <Utensils className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    )}
                    <span>Uber Eats — Receipt</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Newsletter #48</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Plane className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Flight Confirmation · SFO</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Substack Weekly</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ROW 2: Constantly Moves Left <- */}
          <div className="w-0 min-w-full overflow-hidden py-0.5">
            <div className="flex space-x-2 anim-marquee-left-infinite whitespace-nowrap w-max will-change-transform">
              {[0, 1].map((copyIdx) => (
                <React.Fragment key={`r2-${copyIdx}`}>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Package className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Amazon Shipping</span>
                  </div>

                  {/* Starbucks Tile — In-place checkmark replacement on selection */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors duration-300 shrink-0 ${
                    row2Selected
                      ? 'bg-emerald-50/95 text-emerald-900 border-emerald-500 shadow-xs ring-1 ring-emerald-500/20'
                      : 'bg-white/90 border-slate-200/70 text-slate-700'
                  }`}>
                    {row2Selected ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 stroke-[2.5]" />
                    ) : (
                      <Coffee className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    )}
                    <span>Starbucks — Receipt</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Receipt className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Bank Statement Ready</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Utensils className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Dinner Table #40</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ROW 3: Constantly Moves Right -> */}
          <div className="w-0 min-w-full overflow-hidden py-0.5">
            <div className="flex space-x-2 anim-marquee-right-infinite whitespace-nowrap w-max will-change-transform">
              {[0, 1].map((copyIdx) => (
                <React.Fragment key={`r3-${copyIdx}`}>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Calendar className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Calendar Invite</span>
                  </div>

                  {/* Instacart Tile — In-place checkmark replacement on selection */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-colors duration-300 shrink-0 ${
                    row3Selected
                      ? 'bg-emerald-50/95 text-emerald-900 border-emerald-500 shadow-xs ring-1 ring-emerald-500/20'
                      : 'bg-white/90 border-slate-200/70 text-slate-700'
                  }`}>
                    {row3Selected ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 stroke-[2.5]" />
                    ) : (
                      <ShoppingBag className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    )}
                    <span>Instacart — Receipt</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>GitHub Notification</span>
                  </div>

                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/90 border border-slate-200/70 text-slate-600 text-xs font-medium shrink-0">
                    <Receipt className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Lyft Ride Receipt</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM LAYER: Receipt Suggestions (Anchored Title, Downward Card Expansion) */}
        <div className="w-full max-w-full flex-1 flex flex-col justify-start min-w-0">
          
          {/* Header is 100% anchored at fixed position — NEVER shifts */}
          <div className="w-full flex items-center justify-between mb-2 sm:mb-2.5 px-0.5 shrink-0 min-w-0">
            <span className="text-xs sm:text-sm font-bold text-slate-900">
              Receipt Suggestions
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] sm:text-[11px] font-bold transition-all duration-300 ${pillStyle}`}>
              {pillText}
            </span>
          </div>

          {/* Staggered Suggestion Cards — Expanding Downwards into Empty Space */}
          <div className="w-full max-w-full space-y-2 sm:space-y-2.5 min-w-0">
            
            {/* ROW 1: Uber Eats (Populates at Step 1, Expands CTA at Step 4, Fades away at Step 5) */}
            <div className={`w-full max-w-full p-2.5 sm:p-3 rounded-2xl transition-all duration-500 ease-out min-w-0 ${
              showUberEats
                ? uberEatsExpanded
                  ? 'bg-gradient-to-r from-teal-50/95 via-emerald-50/70 to-teal-50/95 border-2 border-teal-500 shadow-xs translate-y-0 opacity-100 scale-100'
                  : 'bg-slate-50/90 border border-slate-100 translate-y-0 opacity-100 scale-100'
                : 'bg-slate-50/40 border border-dashed border-slate-200/80 translate-y-1 opacity-60 scale-[0.99]'
            }`}>
              {!showUberEats ? (
                <div className="flex items-center justify-between py-1 min-w-0 w-full">
                  <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1">
                    <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-2xl bg-slate-200/70 animate-pulse shrink-0" />
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="h-3 w-20 bg-slate-200/80 rounded-md animate-pulse" />
                      <div className="h-2 w-28 bg-slate-100 rounded-md animate-pulse" />
                    </div>
                  </div>
                  <div className="h-3.5 w-12 bg-slate-200/70 rounded-md animate-pulse shrink-0 ml-2" />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between min-w-0 w-full">
                    <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1 mr-2">
                      {/* Uber Eats Brand Squircle with Bold Stacked Mark */}
                      <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-2xl bg-[#06C167] text-white shadow-xs shrink-0 select-none">
                        <div className="flex flex-col items-center justify-center leading-none">
                          <span className="text-[10px] font-black tracking-tight leading-tight">Uber</span>
                          <span className="text-[9px] font-bold tracking-tight leading-tight -mt-0.5 opacity-95">Eats</span>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs sm:text-sm font-bold text-slate-950 truncate">Uber Eats</div>
                        <div className="text-[10px] sm:text-[11px] font-medium text-slate-400 truncate">Dinner Delivery · 8:42 PM</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs sm:text-sm font-black font-mono text-slate-950">$34.50</div>
                      <div className="text-[9px] font-medium text-slate-400">Ready</div>
                    </div>
                  </div>

                  {/* Action Reveal — Naturally Expands Downward with "Create request" */}
                  <div className={`transition-all duration-300 overflow-hidden ${
                    uberEatsExpanded
                      ? 'mt-2 sm:mt-2.5 pt-2 sm:pt-2.5 border-t border-teal-200/60 max-h-12 opacity-100 translate-y-0'
                      : 'max-h-0 opacity-0 pointer-events-none -translate-y-1'
                  }`}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-center py-2 px-3 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs sm:text-sm font-bold shadow-xs transition-transform active:scale-95 cursor-pointer"
                    >
                      <span>Create request</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ROW 2: Starbucks (Populates at Step 2, Fades away at Step 5) */}
            <div className={`w-full max-w-full p-2.5 sm:p-3 rounded-2xl transition-all duration-500 ease-out min-w-0 ${
              showStarbucks
                ? 'bg-slate-50/90 border border-slate-100 translate-y-0 opacity-100 scale-100'
                : 'bg-slate-50/40 border border-dashed border-slate-200/80 translate-y-1 opacity-60 scale-[0.99]'
            }`}>
              {!showStarbucks ? (
                <div className="flex items-center justify-between py-1 min-w-0 w-full">
                  <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1">
                    <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-slate-200/70 animate-pulse shrink-0" />
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="h-3 w-16 bg-slate-200/80 rounded-md animate-pulse" />
                      <div className="h-2 w-24 bg-slate-100 rounded-md animate-pulse" />
                    </div>
                  </div>
                  <div className="h-3.5 w-10 bg-slate-200/70 rounded-md animate-pulse shrink-0 ml-2" />
                </div>
              ) : (
                <div className="flex items-center justify-between min-w-0 w-full">
                  <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1 mr-2">
                    {/* Starbucks Official Siren Circle — White Face & Crown, Green Disc */}
                    <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-white shadow-xs shrink-0 overflow-hidden border border-slate-100/90">
                      <StarbucksSirenSVG className="h-full w-full" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-bold text-slate-950 truncate">Starbucks</div>
                      <div className="text-[10px] sm:text-[11px] font-medium text-slate-400 truncate">Morning Coffee · 7:15 AM</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs sm:text-sm font-black font-mono text-slate-950">$12.75</div>
                    <div className="text-[9px] font-medium text-slate-400">Ready</div>
                  </div>
                </div>
              )}
            </div>

            {/* ROW 3: Instacart (Populates at Step 3, Fades away at Step 5) */}
            <div className={`w-full max-w-full p-2.5 sm:p-3 rounded-2xl transition-all duration-500 ease-out min-w-0 ${
              showInstacart
                ? 'bg-slate-50/90 border border-slate-100 translate-y-0 opacity-100 scale-100'
                : 'bg-slate-50/40 border border-dashed border-slate-200/80 translate-y-1 opacity-60 scale-[0.99]'
            }`}>
              {!showInstacart ? (
                <div className="flex items-center justify-between py-1 min-w-0 w-full">
                  <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1">
                    <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-2xl bg-slate-200/70 animate-pulse shrink-0" />
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="h-3 w-16 bg-slate-200/80 rounded-md animate-pulse" />
                      <div className="h-2 w-28 bg-slate-100 rounded-md animate-pulse" />
                    </div>
                  </div>
                  <div className="h-3.5 w-12 bg-slate-200/70 rounded-md animate-pulse shrink-0 ml-2" />
                </div>
              ) : (
                <div className="flex items-center justify-between min-w-0 w-full">
                  <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1 mr-2">
                    {/* Instacart Official Carrot on Clean Squircle */}
                    <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-2xl bg-[#F4F8F3] shadow-xs shrink-0 border border-slate-100/90 p-1 sm:p-1.5">
                      <InstacartCarrotSVG className="h-full w-full" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-bold text-slate-950 truncate">Instacart</div>
                      <div className="text-[10px] sm:text-[11px] font-medium text-slate-400 truncate">Weekly Groceries · Yesterday</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs sm:text-sm font-black font-mono text-slate-950">$89.40</div>
                    <div className="text-[9px] font-medium text-slate-400">Ready</div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

      </div>

      {/* Infinite Continuous 60fps Keyframe Animations */}
      <style>{`
        @keyframes marqueeRightCont {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0%); }
        }
        @keyframes marqueeLeftCont {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .anim-marquee-right-infinite {
          animation: marqueeRightCont 28s linear infinite;
        }
        .anim-marquee-left-infinite {
          animation: marqueeLeftCont 28s linear infinite;
        }
      `}</style>
    </div>
  );
});

const INNER_ORBIT_LOGOS = [
  { id: 'chase', name: 'Chase', imgClass: 'max-h-[66%] max-w-[76%]' },
  { id: 'chime', name: 'Chime', imgClass: 'max-h-[58%] max-w-[80%]' },
  { id: 'bofa', name: 'Bank of America', imgClass: 'max-h-[60%] max-w-[80%]' },
  { id: 'sofi', name: 'SoFi', imgClass: 'max-h-[58%] max-w-[78%]' },
  { id: 'citi', name: 'Citi', imgClass: 'max-h-[64%] max-w-[78%]' },
  { id: 'schwab', name: 'Charles Schwab', imgClass: 'max-h-[56%] max-w-[80%]' },
  { id: 'truist', name: 'Truist', imgClass: 'max-h-[58%] max-w-[78%]' },
  { id: 'cashapp', name: 'Cash App', imgClass: 'max-h-[70%] max-w-[76%]' },
  { id: 'usaa', name: 'USAA', imgClass: 'max-h-[56%] max-w-[80%]' },
  { id: 'capitalone', name: 'Capital One', imgClass: 'max-h-[58%] max-w-[80%]' },
  { id: 'fidelity', name: 'Fidelity', imgClass: 'max-h-[64%] max-w-[80%]' },
  { id: 'venmo', name: 'Venmo', imgClass: 'max-h-[68%] max-w-[72%]' },
  { id: 'td', name: 'TD Bank', imgClass: 'max-h-[72%] max-w-[76%]' },
  { id: 'discover', name: 'Discover', imgClass: 'max-h-[56%] max-w-[82%]' },
  { id: 'pnc', name: 'PNC Bank', imgClass: 'max-h-[60%] max-w-[80%]' },
  { id: 'robinhood', name: 'Robinhood', imgClass: 'max-h-[68%] max-w-[72%]' },
];

const OUTER_ORBIT_LOGOS = [
  { id: 'wellsfargo', name: 'Wells Fargo', imgClass: 'max-h-[72%] max-w-[80%]' },
  { id: 'ally', name: 'Ally Bank', imgClass: 'max-h-[58%] max-w-[76%]' },
  { id: 'amex', name: 'American Express', imgClass: 'max-h-[72%] max-w-[80%]' },
  { id: 'discover', name: 'Discover', imgClass: 'max-h-[56%] max-w-[82%]' },
  { id: 'cashapp', name: 'Cash App', imgClass: 'max-h-[70%] max-w-[76%]' },
  { id: 'citi', name: 'Citi', imgClass: 'max-h-[64%] max-w-[78%]' },
  { id: 'robinhood', name: 'Robinhood', imgClass: 'max-h-[68%] max-w-[72%]' },
  { id: 'chase', name: 'Chase', imgClass: 'max-h-[66%] max-w-[76%]' },
  { id: 'chime', name: 'Chime', imgClass: 'max-h-[58%] max-w-[80%]' },
  { id: 'usaa', name: 'USAA', imgClass: 'max-h-[56%] max-w-[80%]' },
  { id: 'fidelity', name: 'Fidelity', imgClass: 'max-h-[64%] max-w-[80%]' },
  { id: 'capitalone', name: 'Capital One', imgClass: 'max-h-[58%] max-w-[80%]' },
  { id: 'venmo', name: 'Venmo', imgClass: 'max-h-[68%] max-w-[72%]' },
  { id: 'truist', name: 'Truist', imgClass: 'max-h-[58%] max-w-[78%]' },
  { id: 'bofa', name: 'Bank of America', imgClass: 'max-h-[60%] max-w-[80%]' },
  { id: 'pnc', name: 'PNC Bank', imgClass: 'max-h-[60%] max-w-[80%]' },
  { id: 'schwab', name: 'Charles Schwab', imgClass: 'max-h-[56%] max-w-[80%]' },
  { id: 'sofi', name: 'SoFi', imgClass: 'max-h-[58%] max-w-[78%]' },
];

type GroupMode = 'monthly' | 'trip' | 'ondemand';

interface GroupMemberRow {
  initials: string;
  name: string;
  recipient: string;
  amount: string;
  avatarBg: string;
  avatarText: string;
}

interface GroupModeData {
  id: GroupMode;
  label: string;
  sublabel: string;
  icon: React.ComponentType<{ className?: string }>;
  screenPng: string;
  screenWebp: string;
  screenAvif: string;
  cropPng: string;
  cropWebp: string;
  cropAvif: string;
  settledExplanation: string;
  calendarMonth?: string;
  calendarDay?: string;
  ctaHeadline: string;
  ctaSubtitle: string;
  ctaArrowText: string;
  ctaButtonText: string;
}

const CalendarTile: React.FC<{ month: string; day: string }> = ({ month, day }) => (
  <div className="relative flex flex-col items-center justify-center w-11 h-12 bg-white rounded-xl shadow-md border border-slate-200/90 overflow-hidden shrink-0 select-none">
    {/* Red Top Header Bar */}
    <div className="w-full bg-rose-600 text-white text-[10px] font-black uppercase tracking-wider text-center py-1 leading-none">
      {month}
    </div>
    
    {/* Calendar Day Number */}
    <div className="flex-1 w-full bg-white flex items-center justify-center text-slate-900 font-black text-base font-mono leading-none">
      {day}
    </div>
  </div>
);

const GROUP_MODES: GroupModeData[] = [
  {
    id: 'monthly',
    label: 'Monthly',
    sublabel: 'Automatic every month',
    icon: RotateCw,
    screenPng: groupsMonthlyScreenPng,
    screenWebp: '/screenshots/groups_monthly_screen.webp',
    screenAvif: '/screenshots/groups_monthly_screen.avif',
    cropPng: groupsMonthlyCropPng,
    cropWebp: '/screenshots/groups_monthly_settlement_crop.webp',
    cropAvif: '/screenshots/groups_monthly_settlement_crop.avif',
    settledExplanation: 'Clero automaticaly created payment requests for Alex and John since they were owed money when the monthly group settled.',
    calendarMonth: 'SEPT',
    calendarDay: '3',
    ctaHeadline: 'Simulate settlement.',
    ctaSubtitle: 'See how Clero automatically calculates and dispatches payment requests on the 3rd of each month.',
    ctaArrowText: 'Try it out',
    ctaButtonText: 'Simulate settlement',
  },
  {
    id: 'trip',
    label: 'Trip',
    sublabel: 'Settle on a date',
    icon: Plane,
    screenPng: groupsTripScreenPng,
    screenWebp: '/screenshots/groups_trip_screen.webp',
    screenAvif: '/screenshots/groups_trip_screen.avif',
    cropPng: groupsTripCropPng,
    cropWebp: '/screenshots/groups_trip_settlement_crop.webp',
    cropAvif: '/screenshots/groups_trip_settlement_crop.avif',
    settledExplanation: 'Clero automaticaly created payment requests for Alex and John since they were owed money when the group settled.',
    calendarMonth: 'AUG',
    calendarDay: '25',
    ctaHeadline: 'Simulate settlement.',
    ctaSubtitle: 'See how Clero automatically settles all expenses on August 25th when the trip ends.',
    ctaArrowText: 'Try it out',
    ctaButtonText: 'Simulate settlement',
  },
  {
    id: 'ondemand',
    label: 'On-demand',
    sublabel: 'Settle anytime',
    icon: Zap,
    screenPng: groupsOndemandScreenPng,
    screenWebp: '/screenshots/groups_ondemand_screen.webp',
    screenAvif: '/screenshots/groups_ondemand_screen.avif',
    cropPng: groupsOndemandCropPng,
    cropWebp: '/screenshots/groups_ondemand_settlement_crop.webp',
    cropAvif: '/screenshots/groups_ondemand_settlement_crop.avif',
    settledExplanation: 'Clero created payment requests for Alex and John since they were owed money when the admin settled the group.',
    ctaHeadline: 'Tap to settle the group.',
    ctaSubtitle: 'Watch Clero generate payment requests for everyone owed money in seconds.',
    ctaArrowText: 'Try it out',
    ctaButtonText: 'Settle group now',
  },
];

const GroupsSettlementShowcase = memo(function GroupsSettlementShowcase() {
  const [activeMode, setActiveMode] = useState<GroupMode>('ondemand');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSettled, setIsSettled] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [settledStep, setSettledStep] = useState(0); // 0: unsettled, 1: top explanation, 2: alex phone & title, 3: john phone & title, 4: right cta
  const [isSimulatingDate, setIsSimulatingDate] = useState(false);
  const [simDateText, setSimDateText] = useState('August 1, 2026');
  const [isSimTargetHit, setIsSimTargetHit] = useState(false);
  const [isSimFadingOut, setIsSimFadingOut] = useState(false);

  // Auto-transition timer (pauses permanently once user interacts)
  useEffect(() => {
    if (hasInteracted) return;

    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveMode((prev) => {
          if (prev === 'monthly') return 'trip';
          if (prev === 'trip') return 'ondemand';
          return 'monthly';
        });
        setIsTransitioning(false);
      }, 200);
    }, 4500);

    return () => clearInterval(timer);
  }, [hasInteracted]);

  const handleSelectMode = (mode: GroupMode) => {
    if (mode === activeMode) return;
    setHasInteracted(true);
    setIsTransitioning(true);
    setIsSettled(false);
    setSettledStep(0);
    setIsSimulatingDate(false);
    setIsSimFadingOut(false);
    setTimeout(() => {
      setActiveMode(mode);
      setIsTransitioning(false);
    }, 150);
  };

  const runDateSimulation = (targetMode: 'monthly' | 'trip', onComplete: () => void) => {
    setIsSimulatingDate(true);
    setIsSimTargetHit(false);
    setIsSimFadingOut(false);

    const dates: string[] = [];
    if (targetMode === 'monthly') {
      for (let day = 1; day <= 31; day++) {
        dates.push(`August ${day}, 2026`);
      }
      dates.push('September 1, 2026', 'September 2, 2026', 'September 3, 2026');
    } else {
      for (let day = 1; day <= 25; day++) {
        dates.push(`August ${day}, 2026`);
      }
    }

    let index = 0;
    setSimDateText(dates[0]);

    const interval = setInterval(() => {
      index += 1;
      if (index < dates.length) {
        setSimDateText(dates[index]);
      } else {
        clearInterval(interval);
        setIsSimTargetHit(true);
        // 1. Hold the brand-color date reached state for ~900ms
        setTimeout(() => {
          // 2. Smoothly fade out the date simulation first while section remains compact
          setIsSimFadingOut(true);
          setTimeout(() => {
            // 3. Once fully invisible, expand section and trigger settled phone sequence
            setIsSimulatingDate(false);
            setIsSimTargetHit(false);
            setIsSimFadingOut(false);
            onComplete();
          }, 350);
        }, 900);
      }
    }, 45);
  };

  const startSettlementSequence = () => {
    setIsSettled(true);
    setSettledStep(1); // 1. Top explanation text under headline

    // Generous pacing so viewer has time to process
    setTimeout(() => {
      setSettledStep(2); // 2. Phone 1 (Alex's View + Alex's Phone) fades in & glides up
    }, 800);

    setTimeout(() => {
      setSettledStep(3); // 3. Phone 2 (John's View + John's Phone) fades in & glides up
    }, 1800);

    setTimeout(() => {
      setSettledStep(4); // 4. Right content module with Reset button
    }, 2600);
  };

  const handleToggleSettle = () => {
    setHasInteracted(true);
    if (!isSettled) {
      if (activeMode === 'ondemand') {
        setIsFadingOut(true);
        setTimeout(() => {
          setIsFadingOut(false);
          startSettlementSequence();
        }, 350);
      } else {
        setIsFadingOut(true);
        setTimeout(() => {
          setIsFadingOut(false);
          runDateSimulation(activeMode, () => {
            startSettlementSequence();
          });
        }, 350);
      }
    } else {
      setIsFadingOut(true);
      setTimeout(() => {
        setIsSettled(false);
        setSettledStep(0);
        setIsSimulatingDate(false);
        setIsSimFadingOut(false);
        setIsFadingOut(false);
      }, 350);
    }
  };

  const activeIndex = GROUP_MODES.findIndex((m) => m.id === activeMode);
  const currentModeData = GROUP_MODES[activeIndex] || GROUP_MODES[0];

  return (
    <div className="relative space-y-4 sm:space-y-6">

      {/* DATE SIMULATION OVERLAY: Compact and clean on mobile, full-canvas centered on desktop */}
      <div className={`absolute inset-0 z-20 flex flex-col items-center justify-center transition-opacity duration-300 pointer-events-none ${
        isSimulatingDate && !isSimFadingOut ? 'opacity-100 pointer-events-auto' : 'opacity-0'
      }`}>
        <div className="text-center space-y-2 sm:space-y-3 md:space-y-4 w-full max-w-5xl px-4 flex flex-col items-center justify-center pt-2 sm:pt-4 lg:pt-0">
          <span className="text-xs sm:text-sm md:text-base uppercase font-extrabold tracking-widest text-teal-400 block">
            Simulating Calendar Date
          </span>
          <div className={`text-2xl sm:text-4xl md:text-6xl lg:text-8xl font-black font-mono tracking-tight whitespace-nowrap transition-all duration-500 transform ${
            isSimTargetHit ? 'text-teal-400 scale-105 drop-shadow-sm' : 'text-white scale-100'
          }`}>
            {simDateText}
          </div>
        </div>
      </div>

      <div className="relative z-30 text-center max-w-3xl mx-auto space-y-3 px-2">
        <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl lg:leading-[1.14]">
          Settle on your terms with<br />
          <span className="text-teal-400">Clero Groups.</span>
        </h2>

        {/* Header Sub-Area: Conditionally rendered to guarantee identical height & element spacing across all modes */}
        <div className="pt-1 sm:pt-2">
          
          {/* UNSETTLED: Clean Tab Switcher (No layout shift, strictly identical height across all tabs) */}
          {!isSettled ? (
            <div className={`w-full max-w-xs sm:max-w-md mx-auto transition-opacity duration-300 ${
              !isFadingOut && !isSimulatingDate
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}>
              <div className="relative flex items-center p-1 sm:p-1.5 rounded-2xl sm:rounded-[20px] bg-white/[0.08] border border-white/10 backdrop-blur-md w-full">
                
                <div
                  className="absolute top-1 sm:top-1.5 bottom-1 sm:bottom-1.5 rounded-xl sm:rounded-[15px] bg-white/15 border border-white/20 shadow-xs transition-all duration-300 ease-out"
                  style={{
                    left: `calc(${activeIndex} * (100% / 3) + 3px)`,
                    width: `calc(100% / 3 - 6px)`,
                  }}
                />

                {GROUP_MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isActive = activeMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => handleSelectMode(mode.id)}
                      className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-2 sm:px-3 rounded-xl sm:rounded-[15px] text-xs sm:text-sm font-bold transition-colors duration-200 cursor-pointer ${
                        isActive ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 transition-colors duration-200 ${isActive ? 'text-teal-400' : 'text-slate-400'}`} />
                      <span className="truncate">{mode.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* SETTLED: Sequenced Explanation Subtitle with Real Calendar Tile on the LEFT of text (Step 1) */
            <div className={`w-full max-w-3xl mx-auto text-center px-4 transition-opacity duration-600 ${
              settledStep >= 1 && !isFadingOut && !isSimulatingDate
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}>
              <div className="flex flex-row items-center justify-center gap-3 sm:gap-4 text-left">
                {currentModeData.calendarMonth && currentModeData.calendarDay && (
                  <div className="shrink-0">
                    <CalendarTile
                      month={currentModeData.calendarMonth}
                      day={currentModeData.calendarDay}
                    />
                  </div>
                )}
                <p className="text-sm sm:text-lg font-medium text-slate-200 leading-snug text-left">
                  {activeMode === 'monthly' && (
                    <>
                      Clero automatically created payment requests for{' '}
                      <strong className="font-black text-teal-400">Alex</strong> and{' '}
                      <strong className="font-black text-teal-400">John</strong> since they were owed money when the monthly group settled.
                    </>
                  )}
                  {activeMode === 'trip' && (
                    <>
                      Clero automatically created payment requests for{' '}
                      <strong className="font-black text-teal-400">Alex</strong> and{' '}
                      <strong className="font-black text-teal-400">John</strong> since they were owed money when the group settled.
                    </>
                  )}
                  {activeMode === 'ondemand' && (
                    <>
                      Clero created payment requests for{' '}
                      <strong className="font-black text-teal-400">Alex</strong> and{' '}
                      <strong className="font-black text-teal-400">John</strong> since they were owed money when the admin settled the group.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

        </div>

      </div>

      {/* ========================================================================= */}
      {/* SHOWCASE WRAPPER: 3-Column Showcase with Steady Seamless Canvas           */}
      {/* ========================================================================= */}
      <div className={`relative w-full ${isSimulatingDate ? 'min-h-[140px] sm:min-h-[180px] lg:min-h-0' : ''}`}>

        {/* MAIN 3-COLUMN SHOWCASE (Maintains steady canvas height on desktop, compact on mobile simulation) */}
        <div className={`relative grid grid-cols-1 lg:grid-cols-12 items-start gap-6 lg:gap-8 max-w-7xl mx-auto w-full pt-1 sm:pt-4 pb-0 transition-all duration-300 ${
          isSimulatingDate
            ? 'max-h-0 overflow-hidden lg:max-h-none lg:overflow-visible opacity-0 pointer-events-none'
            : isFadingOut
            ? 'opacity-0 pointer-events-none'
            : 'opacity-100 pointer-events-auto'
        }`}>

          {/* CTA COLUMN: On mobile (<lg), render at top (order-1) so user has interactive control right below tabs */}
          <div className="order-1 lg:order-3 lg:col-span-4 flex flex-col items-center lg:items-start justify-start relative z-30 w-full lg:pt-8 xl:pt-12">
            <div className={`flex flex-col items-center lg:items-start space-y-2 sm:space-y-3 max-w-[340px] w-full transition-opacity duration-500 ${
              (!isSettled && !isFadingOut && !isSimulatingDate) || (isSettled && settledStep >= 4 && !isFadingOut && !isSimulatingDate)
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}>
                
                {/* UNSETTLED ONLY: Eyebrow + Headline + Subtitle + Curved Arrow */}
                {!isSettled && (
                  <>
                    <span className="inline-block text-xs sm:text-sm font-extrabold tracking-widest uppercase text-teal-400">
                      Interactive Demo
                    </span>

                    <div className="space-y-1 text-center lg:text-left min-h-[56px] sm:min-h-[64px] flex flex-col justify-start">
                      <h4 className="text-xl sm:text-2xl lg:text-3xl font-black text-white tracking-tight leading-tight">
                        {currentModeData.ctaHeadline}
                      </h4>
                      <p className="text-xs sm:text-sm font-medium text-slate-400 leading-snug">
                        {currentModeData.ctaSubtitle}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 text-teal-400 font-bold text-xs sm:text-sm pt-0.5">
                      <svg
                        width="30"
                        height="38"
                        viewBox="0 0 34 42"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0 select-none pointer-events-none"
                      >
                        <defs>
                          <linearGradient id="handDrawnPencilGrad" x1="4" y1="2" x2="26" y2="38" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#2DD4BF" stopOpacity="0.35" />
                            <stop offset="50%" stopColor="#2DD4BF" stopOpacity="0.85" />
                            <stop offset="100%" stopColor="#14B8A6" stopOpacity="1" />
                          </linearGradient>
                        </defs>
                        <path
                          d="M6 4 C 18 3, 30 12, 24 32"
                          stroke="url(#handDrawnPencilGrad)"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M15 25 C 20.5 30.5, 23.5 32, 24 32.5 C 26 27, 29.5 24.5, 31 23"
                          stroke="#14B8A6"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-black text-teal-300 tracking-tight text-xs sm:text-sm">
                        {currentModeData.ctaArrowText}
                      </span>
                    </div>
                  </>
                )}

                {/* Big Hero CTA Button */}
                <div className="relative w-full pt-0.5">
                  {!isSettled && (
                    <div className="absolute -inset-1 rounded-2xl bg-teal-400/20 blur-sm animate-pulse pointer-events-none" />
                  )}

                  <button
                    type="button"
                    onClick={handleToggleSettle}
                    className={`relative w-full overflow-hidden flex items-center justify-center gap-3 py-3.5 sm:py-5 px-6 sm:px-8 rounded-2xl font-black text-base sm:text-lg transition-all duration-300 cursor-pointer active:scale-95 shadow-[0_10px_30px_rgba(13,148,136,0.3)] hover:shadow-[0_14px_40px_rgba(13,148,136,0.45)] group ${
                      isSettled
                        ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                        : 'bg-teal-600 hover:bg-teal-500 text-white font-black'
                    }`}
                  >
                    {!isSettled && (
                      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />
                    )}

                    {isSettled ? (
                      <>
                        <RotateCcw className="h-5 w-5 stroke-[2.5]" />
                        <span>Reset preview</span>
                      </>
                    ) : (
                      <>
                        <Zap className="h-5 w-5 fill-white text-white shrink-0" />
                        <span>{currentModeData.ctaButtonText}</span>
                      </>
                    )}
                  </button>
                </div>

              </div>
            </div>

          {/* LEFT COLUMN: Mode Settlement Crop (Unsettled) <---> Alex's POV Phone (Settled) */}
          <div className={`order-3 lg:order-1 lg:col-span-4 flex flex-col items-center lg:items-end relative z-20 w-full lg:pt-0 mb-0 lg:-mb-52 xl:-mb-64 ${
            isSettled ? 'order-2' : 'order-3'
          }`}>
            
            {/* Title Header: Left-aligned where card starts (Unsettled) <---> Centered over Alex Phone (Settled) */}
            <div className="w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[400px] xl:max-w-[420px] pb-2 h-8 grid grid-cols-1 grid-rows-1 items-center">
              
              {/* Unsettled Label - Left aligned where card starts */}
              <div className={`col-start-1 row-start-1 w-full text-center lg:text-left transition-opacity duration-300 ${
                !isSettled && !isFadingOut && !isSimulatingDate
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              }`}>
                <span className="text-xs sm:text-sm font-extrabold tracking-widest uppercase text-teal-400">
                  Settlement Preview
                </span>
              </div>

              {/* Settled Label (Alex's View - Step 2: Smooth Slide-In with Alex Phone) */}
              <div className={`col-start-1 row-start-1 w-full flex items-center justify-center gap-2.5 text-sm sm:text-base font-black tracking-tight text-white transition-all duration-700 ease-out transform ${
                isSettled && settledStep >= 2 && !isFadingOut && !isSimulatingDate
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 translate-y-8 pointer-events-none'
              }`}>
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shrink-0" />
                <span>Alex's View</span>
                <span className="text-emerald-400 font-mono font-black">+$156.60 owed</span>
              </div>
            </div>

            {/* Display Container with CSS Grid (Zero layout shift between Crop and Alex Phone) */}
            <div className="relative w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[400px] xl:max-w-[420px] grid grid-cols-1 grid-rows-1 items-start justify-items-center">
              
              {/* STATE A: Mode Settlement Crop (Visible when NOT settled) */}
              <div className={`col-start-1 row-start-1 w-full max-w-[440px] sm:max-w-[480px] lg:max-w-[500px] transition-opacity duration-300 ${
                !isSettled && !isFadingOut && !isSimulatingDate
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              }`}>
                <div className="rounded-3xl bg-white p-2 sm:p-2.5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] mb-4 sm:mb-6 lg:mb-0">
                  <div className={`transition-all duration-200 ${isTransitioning ? 'opacity-60 scale-[0.98]' : 'opacity-100 scale-100'}`}>
                    <picture>
                      <source srcSet={currentModeData.cropAvif} type="image/avif" />
                      <source srcSet={currentModeData.cropWebp} type="image/webp" />
                      <img
                        alt={`Settlement Preview for ${currentModeData.label}`}
                        className="w-full h-auto rounded-2xl block object-contain"
                        src={currentModeData.cropPng}
                      />
                    </picture>
                  </div>
                </div>
              </div>

              {/* STATE B: Alex's POV Phone Mockup (Step 2: Smooth Slide-In from bottom with bottom fade) */}
              <div
                className={`col-start-1 row-start-1 w-full transition-all duration-700 ease-out transform ${
                  isSettled && settledStep >= 2 && !isFadingOut && !isSimulatingDate
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-20 pointer-events-none'
                }`}
              >
                {/* Outer clipping wrapper for mobile */}
                <div className="relative w-full max-h-[340px] sm:max-h-[420px] lg:max-h-none overflow-hidden rounded-t-[36px] sm:rounded-t-[44px] lg:rounded-none lg:overflow-visible">
                  {/* Mobile Bottom Fade-Out Gradient */}
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#07111B] via-[#07111B]/80 to-transparent z-20 pointer-events-none lg:hidden" />

                  <div className="relative w-full aspect-[1022/2082]">
                    <picture>
                      <source srcSet="/mockup.avif" type="image/avif" />
                      <source srcSet="/mockup.webp" type="image/webp" />
                      <img
                        alt="Alex's POV Settlement Phone"
                        className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.4)]"
                        draggable={false}
                        src="/mockup.png"
                      />
                    </picture>

                    <div
                      className="absolute z-10 overflow-hidden bg-white"
                      style={{
                        left: '5.088%',
                        top: '2.209%',
                        width: '89.824%',
                        height: '95.581%',
                        borderRadius: '13.725% / 6.332%',
                      }}
                    >
                      <picture>
                        <source srcSet="/screenshots/groups_settled_alex_pov.avif" type="image/avif" />
                        <source srcSet="/screenshots/groups_settled_alex_pov.webp" type="image/webp" />
                        <img
                          alt="Alex's POV Settlement View"
                          className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                          draggable={false}
                          src={groupsSettledAlexPovPng}
                        />
                      </picture>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

          {/* MIDDLE COLUMN: Mode Phone (Unsettled) <---> John's POV Phone (Settled) */}
          <div className={`order-2 lg:order-2 lg:col-span-4 flex flex-col items-center justify-center relative z-10 w-full mb-0 lg:-mb-52 xl:-mb-64 ${
            isSettled ? 'order-3' : 'order-2'
          }`}>
            
            {/* Title Header above John's Phone (Step 3: Smooth Slide-In with John Phone) */}
            <div className="w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[400px] xl:max-w-[420px] text-center pb-2 h-8 flex items-center justify-center relative">
              <div className={`flex items-center justify-center gap-2.5 text-sm sm:text-base font-black tracking-tight text-white transition-all duration-700 ease-out transform ${
                isSettled && settledStep >= 3 && !isFadingOut && !isSimulatingDate
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 translate-y-8 pointer-events-none'
              }`}>
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shrink-0" />
                <span>John's View</span>
                <span className="text-emerald-400 font-mono font-black">+$51.30 owed</span>
              </div>
            </div>

            {/* Display Container with CSS Grid for Unified Phone + Screen Fade-In */}
            <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[400px] xl:max-w-[420px] grid grid-cols-1 grid-rows-1 items-start justify-items-center">
              
              {/* UNSETTLED PHONE (Unified Shell + Screen) */}
              <div className={`col-start-1 row-start-1 w-full relative transition-opacity duration-300 ${
                !isSettled && !isFadingOut && !isSimulatingDate
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              }`}>
                {/* Outer clipping wrapper for mobile */}
                <div className="relative w-full max-h-[340px] sm:max-h-[420px] lg:max-h-none overflow-hidden rounded-t-[36px] sm:rounded-t-[44px] lg:rounded-none lg:overflow-visible">
                  {/* Mobile Bottom Fade-Out Gradient */}
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#07111B] via-[#07111B]/80 to-transparent z-20 pointer-events-none lg:hidden" />

                  <div className="relative w-full aspect-[1022/2082]">
                    <picture>
                      <source srcSet="/mockup.avif" type="image/avif" />
                      <source srcSet="/mockup.webp" type="image/webp" />
                      <img
                        alt={`Clero Groups ${currentModeData.label} Screen`}
                        className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.4)]"
                        draggable={false}
                        src="/mockup.png"
                      />
                    </picture>

                    <div
                      className="absolute z-10 overflow-hidden bg-white"
                      style={{
                        left: '5.088%',
                        top: '2.209%',
                        width: '89.824%',
                        height: '95.581%',
                        borderRadius: '13.725% / 6.332%',
                      }}
                    >
                      <picture>
                        <source srcSet={currentModeData.screenAvif} type="image/avif" />
                        <source srcSet={currentModeData.screenWebp} type="image/webp" />
                        <img
                          alt={`Clero Groups ${currentModeData.label} Screen`}
                          className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                          draggable={false}
                          src={currentModeData.screenPng}
                        />
                      </picture>
                    </div>
                  </div>
                </div>
              </div>

              {/* SETTLED PHONE (John's POV: Step 3: Smooth Slide-In from bottom) */}
              <div className={`col-start-1 row-start-1 w-full relative transition-all duration-700 ease-out transform ${
                isSettled && settledStep >= 3 && !isFadingOut && !isSimulatingDate
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 translate-y-20 pointer-events-none'
              }`}>
                {/* Outer clipping wrapper for mobile */}
                <div className="relative w-full max-h-[340px] sm:max-h-[420px] lg:max-h-none overflow-hidden rounded-t-[36px] sm:rounded-t-[44px] lg:rounded-none lg:overflow-visible">
                  {/* Mobile Bottom Fade-Out Gradient */}
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#07111B] via-[#07111B]/80 to-transparent z-20 pointer-events-none lg:hidden" />

                  <div className="relative w-full aspect-[1022/2082]">
                    <picture>
                      <source srcSet="/mockup.avif" type="image/avif" />
                      <source srcSet="/mockup.webp" type="image/webp" />
                      <img
                        alt="John's POV Settlement Phone"
                        className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.4)]"
                        draggable={false}
                        src="/mockup.png"
                      />
                    </picture>

                    <div
                      className="absolute z-10 overflow-hidden bg-white"
                      style={{
                        left: '5.088%',
                        top: '2.209%',
                        width: '89.824%',
                        height: '95.581%',
                        borderRadius: '13.725% / 6.332%',
                      }}
                    >
                      <picture>
                        <source srcSet="/screenshots/groups_settled_john_pov.avif" type="image/avif" />
                        <source srcSet="/screenshots/groups_settled_john_pov.webp" type="image/webp" />
                        <img
                          alt="John's POV Settlement View"
                          className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                          draggable={false}
                          src={groupsSettledJohnPovPng}
                        />
                      </picture>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>

    </div>
  );
});

const AccountsOrbitShowcase = memo(function AccountsOrbitShowcase() {
  return (
    <div className="relative flex flex-col lg:flex-row items-center lg:items-end justify-center lg:justify-start w-full min-h-[580px] sm:min-h-[640px] lg:min-h-[640px] overflow-hidden lg:overflow-visible">
      
      {/* Explicit Inline CSS Keyframes for Reliable Dual Orbit Animation (Paced 68s) */}
      <style>{`
        @keyframes orbitCwAnim {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes orbitCcwAnim {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(-360deg); }
        }
        .clero-orbit-cw {
          animation: orbitCwAnim 68s linear infinite !important;
          will-change: transform;
        }
        .clero-orbit-ccw {
          animation: orbitCcwAnim 68s linear infinite !important;
          will-change: transform;
        }
      `}</style>

      {/* Concentric Rings Origin Point — Centered dome on mobile, shifted origin on desktop */}
      <div className="absolute left-1/2 lg:-left-[140px] top-[75%] sm:top-[68%] lg:top-[48%] -translate-x-1/2 -translate-y-1/2 w-0 h-0 pointer-events-none z-0">
        
        {/* Soft Background Concentric Ripple Bands */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[640px] sm:w-[800px] lg:w-[1200px] h-[640px] sm:h-[800px] lg:h-[1200px] rounded-full bg-cyan-100/30 blur-2xl pointer-events-none" />
        <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[1040px] sm:w-[1200px] lg:w-[1800px] h-[1040px] sm:h-[1200px] lg:h-[1800px] rounded-full bg-cyan-100/20 blur-3xl pointer-events-none" />

        {/* Concentric Deep Guide Rings (Uniform Radii Steps) */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[920px] sm:w-[1200px] lg:w-[2100px] h-[920px] sm:h-[1200px] lg:h-[2100px] rounded-full border border-white/60 pointer-events-none" />
        <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[1060px] sm:w-[1400px] lg:w-[2400px] h-[1060px] sm:h-[1400px] lg:h-[2400px] rounded-full border border-white/35 pointer-events-none" />

        {/* First Active Orbit Track Ring (Inner Ring: 640px mobile / 1500px desktop, Clockwise 68s) */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] sm:w-[800px] sm:h-[800px] lg:w-[1500px] lg:h-[1500px] pointer-events-none">
          <div className="clero-orbit-cw relative w-full h-full rounded-full border-[1.5px] border-white/90 shadow-[0_0_20px_rgba(255,255,255,0.8)]">
            {INNER_ORBIT_LOGOS.map((item, idx) => {
              const angle = idx * (360 / INNER_ORBIT_LOGOS.length);
              const rad = (angle * Math.PI) / 180;
              const x = 50 + 50 * Math.cos(rad);
              const y = 50 + 50 * Math.sin(rad);

              return (
                <div
                  key={idx}
                  className="absolute"
                  style={{
                    left: `${x.toFixed(3)}%`,
                    top: `${y.toFixed(3)}%`,
                    transform: `translate(-50%, -50%) rotate(${angle + 90}deg)`,
                  }}
                >
                  <div className="flex h-12 w-12 sm:h-15 sm:w-15 lg:h-[88px] lg:w-[88px] items-center justify-center rounded-[16px] sm:rounded-[22px] lg:rounded-[28px] bg-white p-2 sm:p-2.5 lg:p-3.5 shadow-[0_8px_20px_rgba(15,23,42,0.10)] border border-slate-100/90 hover:scale-105 transition-transform duration-300 pointer-events-auto">
                    <picture className="w-full h-full flex items-center justify-center pointer-events-none">
                      <source srcSet={`/logos/${item.id}.avif`} type="image/avif" />
                      <source srcSet={`/logos/${item.id}.webp`} type="image/webp" />
                      <img
                        src={`/logos/${item.id}.png`}
                        alt={item.name}
                        className={`${item.imgClass} object-contain pointer-events-none`}
                        draggable={false}
                      />
                    </picture>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Second Active Orbit Track Ring (Outer Ring: 780px mobile / 1800px desktop, Counter-Clockwise 68s) */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[780px] h-[780px] sm:w-[1000px] sm:h-[1000px] lg:w-[1800px] lg:h-[1800px] pointer-events-none">
          <div className="clero-orbit-ccw relative w-full h-full rounded-full border-[1.5px] border-white/80 shadow-[0_0_20px_rgba(255,255,255,0.7)]">
            {OUTER_ORBIT_LOGOS.map((item, idx) => {
              const angle = 10 + idx * (360 / OUTER_ORBIT_LOGOS.length);
              const rad = (angle * Math.PI) / 180;
              const x = 50 + 50 * Math.cos(rad);
              const y = 50 + 50 * Math.sin(rad);

              return (
                <div
                  key={idx}
                  className="absolute"
                  style={{
                    left: `${x.toFixed(3)}%`,
                    top: `${y.toFixed(3)}%`,
                    transform: `translate(-50%, -50%) rotate(${angle + 90}deg)`,
                  }}
                >
                  <div className="flex h-12 w-12 sm:h-15 sm:w-15 lg:h-[88px] lg:w-[88px] items-center justify-center rounded-[16px] sm:rounded-[22px] lg:rounded-[28px] bg-white p-2 sm:p-2.5 lg:p-3.5 shadow-[0_8px_20px_rgba(15,23,42,0.10)] border border-slate-100/90 hover:scale-105 transition-transform duration-300 pointer-events-auto">
                    <picture className="w-full h-full flex items-center justify-center pointer-events-none">
                      <source srcSet={`/logos/${item.id}.avif`} type="image/avif" />
                      <source srcSet={`/logos/${item.id}.webp`} type="image/webp" />
                      <img
                        src={`/logos/${item.id}.png`}
                        alt={item.name}
                        className={`${item.imgClass} object-contain pointer-events-none`}
                        draggable={false}
                      />
                    </picture>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Large Cropped iPhone Mockup positioned on the left (Bevel Style on Desktop, Centered & Pushed Down on Mobile) */}
      <div className="relative z-20 w-full max-w-[260px] sm:max-w-[310px] lg:max-w-[370px] mx-auto lg:mx-0 mt-36 sm:mt-40 lg:mt-0 -mb-48 sm:-mb-52 lg:-mb-48 ml-auto mr-auto lg:ml-2">
        <div
          className="relative mx-auto w-full"
          style={{ aspectRatio: '1022 / 2082' }}
        >
          {/* Authentic iPhone Bezel Base */}
          <picture>
            <source srcSet="/mockup.avif" type="image/avif" />
            <source srcSet="/mockup.webp" type="image/webp" />
            <img
              alt="iPhone Shell Frame"
              className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_30px_60px_rgba(2,8,23,0.25)]"
              draggable={false}
              src="/mockup.png"
            />
          </picture>

          {/* Inner Screen Display with Connected Cards */}
          <div
            className="absolute z-10 overflow-hidden bg-white"
            style={{
              left: '5.088%',
              top: '2.209%',
              width: '89.824%',
              height: '95.581%',
              borderRadius: '13.725% / 6.332%',
            }}
          >
            <picture>
              <source srcSet="/screenshots/accounts_cards_screen.avif" type="image/avif" />
              <source srcSet="/screenshots/accounts_cards_screen.webp" type="image/webp" />
              <img
                alt="Clero Connected Accounts Screen"
                className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                draggable={false}
                src="/screenshots/accounts_cards_screen.png"
              />
            </picture>
          </div>
        </div>
      </div>

    </div>
  );
});

const REVIEWS_DATA = [
  {
    id: 1,
    title: 'No more 1.75% transfer fees',
    author: 'marcus_t, August 30, 2026',
    body: 'Venmo was taking almost $15 every time I cashed out rent money to my bank account. With Clero the money went straight into my Chase account on Friday afternoon with zero fees.',
    rating: 5,
  },
  {
    id: 2,
    title: 'Roommate rent without the spreadsheet',
    author: 'sarah_k_bk, August 29, 2026',
    body: 'We have four people in our Bushwick apartment. WiFi, ConEd, and groceries get added as we spend, and the monthly rollover settled everyone in two transfers on August 1st.',
    rating: 5,
  },
  {
    id: 3,
    title: 'Split our 8-person Tahoe cabin',
    author: 'dan_miller92, August 28, 2026',
    body: 'Booked an Airbnb for a weekend trip. Two friends do not have the app, but they opened the SMS link on their phones and paid from their bank accounts in thirty seconds.',
    rating: 5,
  },
  {
    id: 4,
    title: 'The tax and tip math actually works',
    author: 'elena_r, August 27, 2026',
    body: 'Three of us had drinks and two did not. Clero calculated proportional tax and tip based on what each person ordered instead of making us do manual math at the table.',
    rating: 5,
  },
  {
    id: 5,
    title: 'Card sync saves so much time',
    author: 'jordan.cho, August 25, 2026',
    body: 'Paid for dinner at Lucali on my Amex. When I opened Clero the charge was already sitting there. Tapped three friends and sent the request with itemized totals.',
    rating: 5,
  },
  {
    id: 6,
    title: 'Finally deleted Splitwise',
    author: 'chris_p_sf, August 24, 2026',
    body: 'Splitwise started limiting daily expense entries and charging for basic math. Clero imported our group CSV in one click and handled payments directly.',
    rating: 5,
  },
  {
    id: 7,
    title: 'Uber route splits are clean',
    author: 'maya_lin, August 23, 2026',
    body: 'Took an Uber XL with three stops after a concert. The app read the receipt from my email and split the intermediate stops fairly without argument.',
    rating: 5,
  },
  {
    id: 8,
    title: 'Direct bank deposit is the right model',
    author: 'kevin_b88, August 22, 2026',
    body: 'I hate keeping money sitting in digital wallet balances where it earns zero interest. Money sent in Clero lands in my primary checking account.',
    rating: 5,
  },
];

const ReviewsGalleryShowcase: React.FC = memo(() => {
  return (
    <BottomAppShowcase>
      {/* Continuously Moving Review Tiles Marquee (Calm & Slower Speed) */}
      <div className="relative -mx-6 sm:-mx-10 lg:-mx-14 overflow-hidden pt-4 pb-2">
        {/* Left & Right Edge Fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-28 bg-gradient-to-r from-[#F7FAF9] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-28 bg-gradient-to-l from-[#F2F6F5] to-transparent z-10" />

        <div className="flex w-max gap-4 sm:gap-6 animate-marquee-reviews hover:[animation-play-state:paused] will-change-transform">
          {[...REVIEWS_DATA, ...REVIEWS_DATA].map((review, idx) => (
            <div
              key={`${review.id}-${idx}`}
              className="w-[280px] sm:w-[350px] shrink-0 rounded-3xl bg-white p-6 sm:p-7 border border-slate-200/70 shadow-[0_4px_20px_rgba(15,23,42,0.03)] transition-all hover:shadow-lg hover:border-slate-300 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <h3 className="font-extrabold text-slate-950 text-base sm:text-lg tracking-tight mt-3">
                  {review.title}
                </h3>
                <p className="text-[11px] sm:text-xs font-semibold text-slate-400 mt-1">
                  {review.author}
                </p>
                <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed mt-3.5">
                  "{review.body}"
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BottomAppShowcase>
  );
});

const LandingPage: React.FC<LandingPageProps> = ({ onStartDemo }) => {

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Scroll state for Flighty-style morphing header
  const [isScrolled, setIsScrolled] = useState(false);

  // Page load entrance animation state for hero components
  const [isHeroMounted, setIsHeroMounted] = useState(false);
  const [isPhoneMounted, setIsPhoneMounted] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // Lenis smooth scroll ref
  const lenisRef = useRef<Lenis | null>(null);

  // Hero video player ref
  const heroVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    // Force muted and handle delayed start for hero demonstration once the phone has entered
    if (heroVideoRef.current && isPhoneMounted) {
      heroVideoRef.current.muted = true;
      heroVideoRef.current.defaultMuted = true;
      
      // Delay video start slightly after the phone finishes gliding in
      const startTimer = setTimeout(() => {
        heroVideoRef.current?.play().catch(() => {
          const onFirstTouch = () => {
            heroVideoRef.current?.play();
            window.removeEventListener('touchstart', onFirstTouch);
            window.removeEventListener('click', onFirstTouch);
          };
          window.addEventListener('touchstart', onFirstTouch, { once: true });
          window.addEventListener('click', onFirstTouch, { once: true });
        });
      }, 1000);

      return () => clearTimeout(startTimer);
    }
  }, [isPhoneMounted]);

  const handleVideoEnded = () => {
    if (heroVideoRef.current) {
      // Pause on confirmation screen for 1.5s before restarting loop
      setTimeout(() => {
        if (heroVideoRef.current) {
          heroVideoRef.current.currentTime = 0;
          setTimeout(() => {
            heroVideoRef.current?.play().catch(() => {});
          }, 800);
        }
      }, 1500);
    }
  };

  useEffect(() => {
    // Initialize Lenis smooth inertial scrolling (Bevel / Linear style)
    const lenis = new Lenis({
      duration: 1.3, // Slower, weighted, luxurious scroll duration
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential deceleration
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.8, // Controlled scroll speed (reduced distance per tick for slower glide)
      touchMultiplier: 1.2,
    });

    lenisRef.current = lenis;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    lenis.on('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // Choreographed entrance timing:
    // 1. Text & CTAs mount immediately (+50ms)
    const heroMountTimer = setTimeout(() => {
      setIsHeroMounted(true);
    }, 50);

    // 2. Phone glides in with deliberate delay (+650ms)
    const phoneMountTimer = setTimeout(() => {
      setIsPhoneMounted(true);
    }, 650);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(heroMountTimer);
      clearTimeout(phoneMountTimer);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const navigate = (path: string) => {
    setIsMobileMenuOpen(false);
    window.history.pushState({}, '', path);
  };

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (!element) return;

    if (lenisRef.current) {
      lenisRef.current.scrollTo(element, { offset: -90, duration: 1.3 });
    } else {
      const top = element.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };



  return (
    <PageShell className="overflow-x-hidden">
      {/* CONTINUOUS MORPHING HEADER (FLIGHTY STYLE) */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            {/* Clero Wordmark Logo */}
            <div className="flex items-center gap-2 shrink-0">
              <a
                aria-label="Clero Home"
                className="flex items-center transition opacity-95 hover:opacity-100 py-1"
                href="/"
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            {/* Right Action Area: Help Center + Download App CTA */}
            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 transition-colors whitespace-nowrap px-1 py-1"
                onClick={() => navigate('/support')}
                type="button"
              >
                Help Center
              </button>

              <button
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
                onClick={() => scrollToSection('hero-downloads')}
                type="button"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* Spacing compensation for fixed navbar */}
      <div className="h-16" />

      {/* HERO SECTION: Clean Centered Flighty/Bevel Layout with Flat Professional CTAs */}
      <section className="relative overflow-hidden pt-12 pb-20 lg:pt-18 lg:pb-32">
        <div className="mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8">
          
          {/* Centered Top Stack: Headline, High-Contrast Subheadline, Flat Download CTAs, Trust Proof */}
          <div className="mx-auto max-w-3xl text-center space-y-6">
            
            {/* Centered Headline (Deep High-Contrast Text with Entrance Transition) */}
            <h1
              className={`text-3xl sm:text-5xl lg:text-[4.4rem] font-black tracking-tight text-slate-950 lg:leading-[1.05] break-words transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                isHeroMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Money between friends.
              <br />
              <span className="text-[var(--clero-primary)]">
                Reimagined.
              </span>
            </h1>

            {/* High-Contrast Subheadline (Entrance Transition with Stagger Delay) */}
            <p
              className={`mx-auto max-w-2xl text-base font-medium leading-relaxed text-slate-700 sm:text-lg lg:text-xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] delay-150 ${
                isHeroMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Send and receive money straight from your bank account within the same day* without intermediate wallets or transfer fees. Connect cards, scan receipts with AI, and manage group expenses in one place.
            </p>

            {/* Official App Store & Google Play Badges (Entrance Transition with Stagger Delay) */}
            <div
              className={`flex flex-col items-center justify-center gap-4 pt-2 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] delay-300 ${
                isHeroMounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'
              }`}
              id="hero-downloads"
            >
              <AppStoreBadges badgeHeightClass="h-11 sm:h-12" />

              {/* Product Hunt Featured Badge */}
              <a
                href="https://www.producthunt.com/products/clero-2?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-clero-2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <img
                  alt="Clero - Pay, Request, Split | Product Hunt"
                  width="250"
                  height="54"
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1245288&theme=light&t=1789053424075"
                  className="h-11 sm:h-12 w-auto"
                />
              </a>
            </div>
          </div>

          {/* PRODUCT SHOWCASE: Real Photorealistic iPhone Shell (Smooth Upward Floating Reveal) */}
          <div
            className={`relative mt-12 sm:mt-16 flex flex-col items-center justify-center transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isPhoneMounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-[0.94]'
            }`}
          >
            
            {/* Real iPhone 16 Pro Frame Shell (from mockup.png) */}
            <div
              className="relative mx-auto w-full max-w-[315px] sm:max-w-[340px]"
              style={{ aspectRatio: '1022 / 2082' }}
            >
              <picture>
                <source srcSet="/mockup.avif" type="image/avif" />
                <source srcSet="/mockup.webp" type="image/webp" />
                <img
                  alt="iPhone 16 Pro Frame Shell"
                  className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(2,8,23,0.25)]"
                  draggable={false}
                  src="/mockup.png"
                />
              </picture>

              {/* Inner Screen Display (Exact app-store-screenshots pre-measured dimensions) */}
              <div
                className="absolute z-10 overflow-hidden bg-slate-900"
                style={{
                  left: '5.088%',
                  top: '2.209%',
                  width: '89.824%',
                  height: '95.581%',
                  borderRadius: '13.725% / 6.332%',
                }}
              >
                {/* Instant Static First Frame Poster (Zero Black Screen Flicker) */}
                <picture>
                  <source srcSet="/videos/batchpay_poster.avif" type="image/avif" />
                  <source srcSet="/videos/batchpay_poster.webp" type="image/webp" />
                  <img
                    alt="Clero Batch Pay Screen"
                    className="absolute inset-0 block h-full w-full object-cover object-top z-10 pointer-events-none"
                    draggable={false}
                    src="/videos/batchpay_poster.png"
                  />
                </picture>

                {/* Hardware-Accelerated Video Layer (Fades In Smoothly) */}
                <video
                  ref={heroVideoRef}
                  className={`absolute inset-0 block h-full w-full object-cover object-top z-20 transition-opacity duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    isVideoReady ? 'opacity-100' : 'opacity-0'
                  }`}
                  muted
                  onEnded={handleVideoEnded}
                  onPlaying={() => setIsVideoReady(true)}
                  playsInline
                  preload="auto"
                >
                  <source src="/videos/batchpay.mp4" type="video/mp4" />
                </video>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* INSTITUTIONAL BANKING INFRASTRUCTURE & SECURITY METRICS BAR */}
      <section className="bg-slate-50/70 py-6 sm:py-8">
        <div className="mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            
            {/* Astra Banking Partner */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] transition hover:shadow-md">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">
                <AstraMarkOnly className="h-5 sm:h-5.5 w-auto" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Banking Partner</p>
                <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">Astra Financial</p>
              </div>
            </div>

            {/* Plaid Verification */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] transition hover:shadow-md">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">
                <PlaidMarkOnly className="h-4 sm:h-5 w-auto" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Bank Authentication</p>
                <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">Verified by Plaid</p>
              </div>
            </div>

            {/* Bank-Grade Security */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] transition hover:shadow-md">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-[var(--clero-primary)]">
                <ShieldCheck className="h-5 sm:h-5.5 w-5 sm:w-5.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Bank-Grade</p>
                <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">256-Bit Encryption</p>
              </div>
            </div>

            {/* Direct to Bank & No Wallet Transfer Fees */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] transition hover:shadow-md">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 font-black text-sm sm:text-base">
                $0
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Direct to Bank</p>
                <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">No Wallet Transfer Fees</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* MAIN FEATURES SHOWCASE: BENTO BREAKDOWN + 2-PHONE DIRECT PAY & REQUEST */}
      <section className="pt-6 sm:pt-8 pb-8 sm:pb-12 bg-white" id="features">
        <div className="mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
          
          {/* Bento Card 1: Live Network Momentum & Traction Metrics */}
          <div className="overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#F0FDF4] via-[#F8FAFC] to-[#EEF5F2] p-6 sm:p-10 lg:p-14">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
              
              {/* Left Headline & Story — Clean, no pills */}
              <div className="space-y-4 lg:col-span-5 z-10 self-center">
                <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
                  Real money moved.<br className="hidden sm:inline" /> Zero wallet lag.
                </h2>
                <p className="text-base sm:text-lg font-medium text-slate-600 leading-relaxed max-w-md">
                  Friends across the country use Clero to settle dinners, split group trips, and transfer directly between checking accounts with zero cashout penalties.
                </p>
              </div>

              {/* Right Side: Live Metrics Showcase Grid */}
              <div className="lg:col-span-7 flex justify-center w-full">
                <LiveMetricsShowcase />
              </div>

            </div>
          </div>

          {/* 2 Feature Cards: Direct Pay & Request Money */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            
            {/* Phone Card 1: Direct Pay */}
            <div className="relative flex flex-col justify-between overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-b from-[#EBF7F4] via-[#F2FAF7] to-[#F8FCFA] p-7 sm:p-10 pt-9 sm:pt-12 min-h-[580px] transition-all duration-300 hover:from-[#E4F5F1] hover:to-[#F0F8F5]">
              {/* Card Header */}
              <div className="space-y-3 z-10">
                <h3 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                  Direct Pay
                </h3>
                <p className="text-sm sm:text-base font-medium text-slate-600 leading-relaxed max-w-md">
                  Send money straight to any checking account with zero cashout penalties or wallet balance delays.
                </p>
              </div>

              {/* Bottom Phone Frame Rising with Photorealistic Shell & Pop-Out Chase Crop */}
              <div className="relative mt-8 -mb-16 sm:-mb-24 flex justify-center w-full">
                
                {/* Real iPhone Frame Shell */}
                <div
                  className="relative mx-auto w-full max-w-[280px] sm:max-w-[310px]"
                  style={{ aspectRatio: '1022 / 2082' }}
                >
                  {/* Authentic iPhone Bezel Base */}
                  <picture>
                    <source srcSet="/mockup.avif" type="image/avif" />
                    <source srcSet="/mockup.webp" type="image/webp" />
                    <img
                      alt="iPhone Shell Frame"
                      className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(2,8,23,0.22)]"
                      draggable={false}
                      src="/mockup.png"
                    />
                  </picture>

                  {/* Inner Screen Display */}
                  <div
                    className="absolute z-10 overflow-hidden bg-white"
                    style={{
                      left: '5.088%',
                      top: '2.209%',
                      width: '89.824%',
                      height: '95.581%',
                      borderRadius: '13.725% / 6.332%',
                    }}
                  >
                    <picture>
                      <source srcSet="/screenshots/direct_pay_screen.avif" type="image/avif" />
                      <source srcSet="/screenshots/direct_pay_screen.webp" type="image/webp" />
                      <img
                        alt="Clero Direct Pay Screen"
                        className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                        draggable={false}
                        src="/screenshots/direct_pay_screen.png"
                      />
                    </picture>
                  </div>

                  {/* Floating Chase Checking Crop (Direct Raw Pop-Out with Subtle Soft Shadow) */}
                  <div className="absolute top-[43%] left-1/2 -translate-x-1/2 w-[104%] sm:w-[106%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                    <picture>
                      <source srcSet="/screenshots/floating_chase_badge.avif" type="image/avif" />
                      <source srcSet="/screenshots/floating_chase_badge.webp" type="image/webp" />
                      <img
                        alt="Chase Checking Direct"
                        className="h-auto w-full rounded-2xl drop-shadow-[0_12px_24px_rgba(15,23,42,0.16)]"
                        src="/screenshots/floating_chase_badge.png"
                      />
                    </picture>
                  </div>
                </div>

              </div>
            </div>

            {/* Phone Card 2: Request Money */}
            <div className="relative flex flex-col justify-between overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-b from-[#EFF3F9] via-[#F5F7FC] to-[#F9FAFD] p-7 sm:p-10 pt-9 sm:pt-12 min-h-[580px] transition-all duration-300 hover:from-[#E8EEF7] hover:to-[#F2F5F9]">
              {/* Card Header */}
              <div className="space-y-3 z-10">
                <h3 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                  Request Money
                </h3>
                <p className="text-sm sm:text-base font-medium text-slate-600 leading-relaxed max-w-md">
                  Request any amount from a friend with custom notes, instant web payment links, and zero fees.
                </p>
              </div>

              {/* Bottom Phone Frame Rising with Photorealistic Shell & Pop-Out Crops */}
              <div className="relative mt-8 -mb-16 sm:-mb-24 flex justify-center w-full">
                
                {/* Real iPhone Frame Shell */}
                <div
                  className="relative mx-auto w-full max-w-[280px] sm:max-w-[310px]"
                  style={{ aspectRatio: '1022 / 2082' }}
                >
                  {/* Authentic iPhone Bezel Base */}
                  <picture>
                    <source srcSet="/mockup.avif" type="image/avif" />
                    <source srcSet="/mockup.webp" type="image/webp" />
                    <img
                      alt="iPhone Shell Frame"
                      className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(2,8,23,0.22)]"
                      draggable={false}
                      src="/mockup.png"
                    />
                  </picture>

                  {/* Inner Screen Display */}
                  <div
                    className="absolute z-10 overflow-hidden bg-white"
                    style={{
                      left: '5.088%',
                      top: '2.209%',
                      width: '89.824%',
                      height: '95.581%',
                      borderRadius: '13.725% / 6.332%',
                    }}
                  >
                    <picture>
                      <source srcSet="/screenshots/request_setup_screen.avif" type="image/avif" />
                      <source srcSet="/screenshots/request_setup_screen.webp" type="image/webp" />
                      <img
                        alt="Clero Request Setup Screen"
                        className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                        draggable={false}
                        src="/screenshots/request_setup_screen.png"
                      />
                    </picture>
                  </div>

                  {/* Floating Groceries Summary Crop (Direct Raw Pop-Out with Subtle Soft Shadow) */}
                  <div className="absolute top-[17%] left-1/2 -translate-x-1/2 w-[104%] sm:w-[106%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                    <picture>
                      <source srcSet="/screenshots/floating_groceries_badge.avif" type="image/avif" />
                      <source srcSet="/screenshots/floating_groceries_badge.webp" type="image/webp" />
                      <img
                        alt="Groceries Request Summary"
                        className="h-auto w-full rounded-2xl drop-shadow-[0_12px_24px_rgba(15,23,42,0.16)]"
                        src="/screenshots/floating_groceries_badge.png"
                      />
                    </picture>
                  </div>

                  {/* Floating Participants Crop (Direct Raw Pop-Out with Subtle Soft Shadow) */}
                  <div className="absolute top-[53%] left-1/2 -translate-x-1/2 w-[104%] sm:w-[106%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                    <picture>
                      <source srcSet="/screenshots/floating_participants_badge.avif" type="image/avif" />
                      <source srcSet="/screenshots/floating_participants_badge.webp" type="image/webp" />
                      <img
                        alt="Participants Split Shares"
                        className="h-auto w-full rounded-2xl drop-shadow-[0_12px_24px_rgba(15,23,42,0.16)]"
                        src="/screenshots/floating_participants_badge.png"
                      />
                    </picture>
                  </div>
                </div>

              </div>
            </div>

          </div>

          {/* Bento Card: Connect all your accounts (Bevel Style: Large Cropped Phone, Big Orbiting Logos, Text Right) */}
          <div className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#EBF5F8] via-[#F3F8FA] to-[#EDF6F8] p-6 sm:p-10 lg:p-14 pb-0 sm:pb-0 lg:pb-0">
            
            <div className="relative z-10 grid gap-6 sm:gap-8 lg:gap-10 lg:grid-cols-12 lg:items-center">
              
              {/* Display Headline & Text — Top on Mobile (order-1), Right on Desktop (order-2 lg:col-span-5) */}
              <div className="order-1 lg:order-2 space-y-3 sm:space-y-4 lg:col-span-5 z-20 self-center pt-2 sm:pt-4 lg:pt-0 pb-0 lg:pb-14 lg:pl-6 text-left lg:ml-auto max-w-sm">
                <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-[2.85rem] lg:leading-[1.12]">
                  Connect all<br className="hidden sm:inline" /> your accounts.
                </h2>
                <p className="text-base sm:text-lg font-medium text-slate-600 leading-relaxed">
                  Link your checking accounts and credit cards to view posted charges, split group dinners, and start payment requests with a single tap.
                </p>
              </div>

              {/* Showcase & Orbits — Bottom on Mobile (order-2), Left on Desktop (order-1 lg:col-span-7) */}
              <div className="order-2 lg:order-1 lg:col-span-7 flex items-end justify-center lg:justify-start w-full">
                <AccountsOrbitShowcase />
              </div>

            </div>
          </div>

          {/* ========================================================================= */}
          {/* SECTION: Smart Requests (Transactions & Subscriptions Dual-Bento)         */}
          {/* ========================================================================= */}
          <div id="smart-requests" className="space-y-6 sm:space-y-8 pt-2 sm:pt-4">
            
            {/* Subtle Downward Directional Connector Arrow */}
            <div className="flex justify-center items-center pb-2 sm:pb-4">
              <svg
                width="28"
                height="44"
                viewBox="0 0 28 44"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="opacity-70 select-none pointer-events-none"
              >
                <defs>
                  <linearGradient id="downArrowGrad" x1="14" y1="0" x2="14" y2="36" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#10B981" stopOpacity="0" />
                    <stop offset="60%" stopColor="#10B981" stopOpacity="0.75" />
                    <stop offset="100%" stopColor="#059669" stopOpacity="0.95" />
                  </linearGradient>
                </defs>
                <path
                  d="M14 2V36"
                  stroke="url(#downArrowGrad)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M6.5 29L14 36.5L21.5 29"
                  stroke="#059669"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Section Header (Clean typography, all-black title, no generic badge, no em dash) */}
            <div className="text-center max-w-3xl mx-auto space-y-3 sm:space-y-4 px-2">
              <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-5xl lg:leading-[1.14]">
                Create requests from transactions and subscriptions.
              </h2>
              <p className="text-base sm:text-lg font-medium text-slate-600 leading-relaxed max-w-2xl mx-auto">
                Turn charges into instant requests. Whether it's a bunch of charges or subscriptions, Clero makes it easy to split, track, and get paid back.
              </p>
            </div>

            {/* Dual Bento Cards Grid */}
            <div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
              
              {/* CARD A: Transactions (Posted Charges -> Instant Requests) */}
              <div className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#F0FDF4] via-[#F6FCF8] to-[#EBF7F0] p-7 sm:p-9 lg:p-10 pb-10 sm:pb-12 lg:pb-12 flex flex-col justify-between min-w-0">
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8 items-center h-full">
                  
                  {/* Left Column: Colored Eyebrow Label + Headline + Subtitle */}
                  <div className="md:col-span-5 lg:col-span-5 space-y-3 sm:space-y-4 z-10 py-2 sm:py-4">
                    <span className="inline-block text-xs sm:text-sm font-extrabold tracking-widest uppercase text-emerald-600">
                      Transactions
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight leading-tight">
                      Turn posted charges into requests.
                    </h3>
                    <p className="text-sm sm:text-base font-medium text-slate-600 leading-relaxed">
                      Select any transaction posted on your connected credit cards or bank accounts, select friends, and send a request in seconds.
                    </p>
                  </div>

                  {/* Right Column: Phone with Expanded Crops */}
                  <div className="md:col-span-7 lg:col-span-7 flex justify-center items-center py-2 sm:py-4">
                    <div
                      className="relative mx-auto w-full max-w-[260px] sm:max-w-[280px]"
                      style={{ aspectRatio: '1022 / 2082' }}
                    >
                      {/* Authentic iPhone Frame Shell */}
                      <picture>
                        <source srcSet="/mockup.avif" type="image/avif" />
                        <source srcSet="/mockup.webp" type="image/webp" />
                        <img
                          alt="iPhone Shell Frame"
                          className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(2,8,23,0.18)]"
                          draggable={false}
                          src="/mockup.png"
                        />
                      </picture>

                      {/* Inner Screen Display (z-10 above bezel base) */}
                      <div
                        className="absolute z-10 overflow-hidden bg-white"
                        style={{
                          left: '5.088%',
                          top: '2.209%',
                          width: '89.824%',
                          height: '95.581%',
                          borderRadius: '13.725% / 6.332%',
                        }}
                      >
                        <picture>
                          <source srcSet="/screenshots/transactions_screen.avif" type="image/avif" />
                          <source srcSet="/screenshots/transactions_screen.webp" type="image/webp" />
                          <img
                            alt="Clero Transactions Screen"
                            className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                            draggable={false}
                            src="/screenshots/transactions_screen.png"
                          />
                        </picture>
                      </div>

                      {/* Floating Touchstone Row Crop (Mid-Screen Pop-Out) */}
                      <div className="absolute top-[53%] left-1/2 -translate-x-1/2 w-[110%] sm:w-[114%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                        <img
                          alt="Touchstone Climbing Selected Row"
                          className="h-auto w-full rounded-2xl drop-shadow-[0_16px_32px_rgba(15,23,42,0.18)]"
                          draggable={false}
                          src={touchstoneRowCropPng}
                        />
                      </div>

                      {/* Floating Touchstone Summary Crop (Covering bottom bezel of phone) */}
                      <div className="absolute -bottom-4 sm:-bottom-5 left-1/2 -translate-x-1/2 w-[110%] sm:w-[114%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                        <img
                          alt="Touchstone Climbing Summary"
                          className="h-auto w-full rounded-2xl drop-shadow-[0_16px_36px_rgba(15,23,42,0.18)]"
                          draggable={false}
                          src={touchstoneClimbingCropPng}
                        />
                      </div>

                    </div>
                  </div>

                </div>

              </div>

              {/* CARD B: Subscriptions (Recurring Bills -> Split Effortlessly) */}
              <div className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#EEF4FF] via-[#F5F8FE] to-[#E9F0FD] p-7 sm:p-9 lg:p-10 pb-10 sm:pb-12 lg:pb-12 flex flex-col justify-between min-w-0">
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8 items-center h-full">
                  
                  {/* Left Column: Colored Eyebrow Label + Headline + Subtitle */}
                  <div className="md:col-span-5 lg:col-span-5 space-y-3 sm:space-y-4 z-10 py-2 sm:py-4">
                    <span className="inline-block text-xs sm:text-sm font-extrabold tracking-widest uppercase text-indigo-600">
                      Subscriptions
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight leading-tight">
                      Split bills or subscriptions effortlessly.
                    </h3>
                    <p className="text-sm sm:text-base font-medium text-slate-600 leading-relaxed">
                      Clero detects repeating charges on your linked credit cards or bank accounts, making it easy for you to select and set up repeating requests.
                    </p>
                  </div>

                  {/* Right Column: Phone with Expanded Crops */}
                  <div className="md:col-span-7 lg:col-span-7 flex justify-center items-center py-2 sm:py-4">
                    <div
                      className="relative mx-auto w-full max-w-[260px] sm:max-w-[280px]"
                      style={{ aspectRatio: '1022 / 2082' }}
                    >
                      {/* Authentic iPhone Frame Shell */}
                      <picture>
                        <source srcSet="/mockup.avif" type="image/avif" />
                        <source srcSet="/mockup.webp" type="image/webp" />
                        <img
                          alt="iPhone Shell Frame"
                          className="pointer-events-none block h-full w-full object-contain drop-shadow-[0_25px_50px_rgba(2,8,23,0.18)]"
                          draggable={false}
                          src="/mockup.png"
                        />
                      </picture>

                      {/* Inner Screen Display (z-10 above bezel base) */}
                      <div
                        className="absolute z-10 overflow-hidden bg-white"
                        style={{
                          left: '5.088%',
                          top: '2.209%',
                          width: '89.824%',
                          height: '95.581%',
                          borderRadius: '13.725% / 6.332%',
                        }}
                      >
                        <picture>
                          <source srcSet="/screenshots/recurring_expense_details_screen.avif" type="image/avif" />
                          <source srcSet="/screenshots/recurring_expense_details_screen.webp" type="image/webp" />
                          <img
                            alt="Clero Recurring Expense Details Screen"
                            className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
                            draggable={false}
                            src="/screenshots/recurring_expense_details_screen.png"
                          />
                        </picture>
                      </div>

                      {/* Floating Expense Details Crop (Top-Screen Pop-Out Aligned with Expense details) */}
                      <div className="absolute top-[7%] left-1/2 -translate-x-1/2 w-[110%] sm:w-[114%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                        <img
                          alt="Recurring Request Details"
                          className="h-auto w-full rounded-2xl drop-shadow-[0_16px_32px_rgba(15,23,42,0.18)]"
                          draggable={false}
                          src={recurringDetailsCropPng}
                        />
                      </div>

                      {/* Floating Hulu Subscription Crop (Covering bottom bezel of phone) */}
                      <div className="absolute -bottom-4 sm:-bottom-5 left-1/2 -translate-x-1/2 w-[110%] sm:w-[114%] z-30 pointer-events-none transition-transform duration-300 hover:scale-105">
                        <img
                          alt="Hulu Subscription"
                          className="h-auto w-full rounded-2xl drop-shadow-[0_16px_32px_rgba(15,23,42,0.18)]"
                          draggable={false}
                          src={huluSubscriptionCropPng}
                        />
                      </div>

                    </div>
                  </div>

                </div>

              </div>

            </div>
          </div>

          {/* Bento Card 2: Scan receipts. Assign items. */}
          <div className="overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#EEF3FF] via-[#F4F7FE] to-[#EBF3FC] p-6 sm:p-10 lg:p-14">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
              
              {/* Left Display Headline & Text — Vertically Centered */}
              <div className="space-y-4 lg:col-span-5 z-10 self-center">
                <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
                  Scan receipts.<br className="hidden sm:inline" /> Assign items.
                </h2>
                <p className="text-base sm:text-lg font-medium text-slate-600 leading-relaxed max-w-md">
                  Snap, upload, or share a bill to auto-detect items. Tap to assign dishes or let friends claim their own later, with tax and tip split proportionally.
                </p>
              </div>

              {/* Right Side: Proportional 2-Column Showcase (Top & Bottom Aligned) */}
              <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-[1fr_1.08fr] gap-5 sm:gap-6 lg:gap-7 items-start justify-center max-w-[660px] mx-auto w-full">
                
                {/* Column 1 (Left): Isolated Zero-Jank Scanned Bill Widget */}
                <ReceiptScannerWidget />

                {/* Column 2 (Right): Stacked Summary Card & Assigned Items (Scaled to Align with Bottom of Progress Bar) */}
                <div className="flex flex-col justify-start space-y-3.5 w-full">
                  {/* Top: Detected Total Summary Card */}
                  <div className="w-full">
                    <picture className="block w-full">
                      <source srcSet="/screenshots/detected_cafe_card.avif" type="image/avif" />
                      <source srcSet="/screenshots/detected_cafe_card.webp" type="image/webp" />
                      <img
                        alt="Detected Cafe du Soleil Total Summary"
                        className="block h-auto w-full object-contain pointer-events-none"
                        src="/screenshots/detected_cafe_card.png"
                      />
                    </picture>
                  </div>

                  {/* Bottom: Assigned Split Items Crop */}
                  <div className="w-full">
                    <picture className="block w-full">
                      <source srcSet="/screenshots/assigned_items_crop.avif" type="image/avif" />
                      <source srcSet="/screenshots/assigned_items_crop.webp" type="image/webp" />
                      <img
                        alt="Assigned Split Items Crop"
                        className="block h-auto w-full object-contain pointer-events-none"
                        src="/screenshots/assigned_items_crop.png"
                      />
                    </picture>
                  </div>
                </div>

              </div>

            </div>
          </div>

          {/* Bento Card: Claim your items. Settle in one tap. (Participant Experience + Live Organizer Sync) */}
          <div className="overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#F0FDF4] via-[#F7FCF9] to-[#E8F8F0] p-6 sm:p-10 lg:p-14">
            <div className="grid gap-8 lg:gap-12 lg:grid-cols-12 lg:items-center">
              
              {/* Left Showcase (iPhone Playing claimandpay.mp4 + Live Organizer SplitHeroCard Synchronized) */}
              <div className="order-2 lg:order-1 lg:col-span-7 flex items-center justify-center w-full">
                <ClaimAndSettleShowcase />
              </div>

              {/* Right Display Headline & Text — order-1 on mobile, order-2 lg:col-span-5 on desktop */}
              <div className="order-1 lg:order-2 space-y-3 sm:space-y-4 lg:col-span-5 z-10 self-center text-left lg:ml-auto max-w-md">
                <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-[2.6rem] lg:leading-[1.12]">
                  Friends claim items.<br className="hidden sm:inline" /> You track progress.
                </h2>
                <p className="text-base sm:text-lg font-medium text-slate-600 leading-relaxed">
                  After claiming items, friends can instantly pay with funds landing directly in your bank account. You can see and track all updates in real time: item claims, who has paid, and remaining balances.
                </p>
              </div>

            </div>
          </div>

          {/* Bento Card 4: Gmail Receipt Suggestions (Text Left, Receipt-Sorting Machine Right) */}
          <div className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] bg-gradient-to-br from-[#F5F8FA] via-[#F0F5F8] to-[#EDF6F8] p-5 sm:p-10 lg:p-14">
            <div className="relative z-10 grid gap-8 sm:gap-10 lg:gap-12 lg:grid-cols-12 lg:items-center min-w-0">
              
              {/* Display Headline & Text — Left on Desktop (order-1 lg:col-span-5), Top on Mobile (order-1) */}
              <div className="order-1 lg:order-1 space-y-3 sm:space-y-4 lg:col-span-5 z-10 self-center text-left max-w-md w-full min-w-0">
                <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-[2.6rem] lg:leading-[1.12]">
                  Your inbox already<br />has the receipts.
                </h2>
                <p className="text-base sm:text-lg font-medium text-slate-600 leading-relaxed">
                  Connect up to three Gmail accounts. When receipt emails show up in your inbox, Clero finds and turns them into expenses ready for you to request or split with friends.
                </p>
              </div>

              {/* Showcase Visual — Right on Desktop (order-2 lg:col-span-7), Bottom on Mobile (order-2) */}
              <div className="order-2 lg:order-2 lg:col-span-7 flex items-center justify-center w-full min-w-0">
                <GmailReceiptMachineShowcase />
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION: Groups Showcase (Full Width Brand Dark Background)              */}
      {/* ========================================================================= */}
      <section id="groups" className="relative overflow-hidden bg-[#07111B] text-white py-16 sm:py-20 lg:py-24">
        {/* Subtle Ambient Teal & Cyan Radial Glows */}
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[800px] rounded-full bg-teal-500/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 right-1/4 h-[400px] w-[600px] rounded-full bg-cyan-500/8 blur-[100px]" />

        <div className="relative z-10 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8">
          <GroupsSettlementShowcase />
        </div>
      </section>

      {/* ========================================================================= */}
      {/* SECTION: Reviews & Social Proof Showcase (Crafted with Care)             */}
      {/* ========================================================================= */}
      <section id="reviews" className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8">
          <ReviewsGalleryShowcase />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[var(--clero-max-content)]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
              <div className="pt-1">
                <a
                  href="https://www.producthunt.com/products/clero-2?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-clero-2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform duration-200 hover:scale-105 active:scale-95"
                >
                  <img
                    alt="Clero - Pay, Request, Split | Product Hunt"
                    width="250"
                    height="54"
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1245288&theme=light&t=1789053424075"
                    className="h-10 w-auto"
                  />
                </a>
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">
                    All Splitting Apps
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">
                    Clero vs Splitwise
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">
                    Clero vs Venmo
                  </button>
                </li>
                <li>
                  <button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">
                    Clero vs Zelle
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => scrollToSection('features')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => scrollToSection('faq')} type="button">FAQ</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 space-y-3 text-center text-xs text-slate-400">
            <p className="max-w-3xl mx-auto leading-relaxed text-slate-400/90">
              * Same-day bank transfers are processed via Same Day ACH on banking business days (Monday–Friday, excluding federal holidays). Transfers initiated before 2:00 PM EST on business days typically settle the same day. Transfers initiated after 2:00 PM EST, on weekends, or on federal banking holidays settle on the next business day (e.g. transfers initiated on Friday afternoon or over the weekend settle on Monday). Settlement timing may vary depending on your bank.
            </p>
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};

export default LandingPage;
